import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import FeatureHeader from "../components/FeatureHeader";
import {
  Bell,
  MoreVertical,
  ShieldCheck,
  Smartphone,
  Users,
  HeartPulse,
  Droplet,
  Heart,
  Thermometer,
  Activity,
  Scale,
  ClipboardList,
  Pill,
  Check,
  Circle,
  Sparkles,
  Stethoscope,
  MessageCircle,
  Share2,
  FileText,
  X,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Edit2,
  Plus,
  AlertCircle,
  Clock,
  Siren,
  Phone,
  UserRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import BottomNavigation from "../components/bottom_navigation";
import api from "../lib/api";
import { useNotifications } from "../contexts/NotificationContext";
import AddMedicationModal from "../components/medicationmodal";
import MedicationModal from "../components/medicationmodal";
import { emitHealthDataUpdated, onHealthDataUpdated } from "../lib/healthSync";

const C = {
  primary: "#7f00ff",
  primaryFg: "#ffffff",
  primarySoft: "#f1e6ff",
  primarySofter: "#faf5ff",
  bg: "#ffffff",
  fg: "#0f172a",
  mutedFg: "#6b7a82",
  border: "#eceef1",
  shellBg: "#f4f5f7",
  page: "#fbfbfd",
  good: "#16a34a",
  goodSoft: "#dcfce7",
  warn: "#f59e0b",
  warnSoft: "#fef3c7",
  pink: "#fee2e2",
  pinkFg: "#ef4444",
  blue: "#dbeafe",
  blueFg: "#2563eb",
  peach: "#ffedd5",
  peachFg: "#f97316",
  danger: "#ef4444",
  dangerSoft: "#fee2e2",
};

const row: CSSProperties = { display: "flex", alignItems: "center" };
const col: CSSProperties = { display: "flex", flexDirection: "column" };

const CARE_PLAN_TASK_ORDER = ["bp", "hydration", "medication", "symptoms", "followUpQuestions"];

const DEFAULT_TASK_LABELS: Record<string, string> = {
  bp: "Log Blood Pressure",
  hydration: "Hydration Check",
  medication: "Take Medication",
  symptoms: "Log Symptoms",
  followUpQuestions: "Complete Follow-up Questions",
};

function Chip({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div style={{ ...row, gap: 6, flex: 1, justifyContent: "center", padding: "10px 8px" }}>
      <span style={{ color: C.primary, display: "flex" }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: C.fg }}>{label}</span>
    </div>
  );
}

function VitalCard({
  icon,
  iconBg,
  iconFg,
  label,
  value,
  unit,
  status,
  statusColor,
  statusBg,
  time,
  onClick,
}: {
  icon: ReactNode;
  iconBg: string;
  iconFg: string;
  label: string;
  value: string;
  unit?: string;
  status: string;
  statusColor: string;
  statusBg: string;
  time: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        ...col,
        gap: 8,
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: 12,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={{ ...row, justifyContent: "space-between" }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 9999,
            background: iconBg,
            color: iconFg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.fg }}>{label}</span>
      </div>
      <div style={{ ...row, gap: 4, alignItems: "baseline" }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: C.fg }}>{value}</span>
        {unit ? <span style={{ fontSize: 11, color: C.mutedFg }}>{unit}</span> : null}
      </div>
      <span
        style={{
          alignSelf: "flex-start",
          fontSize: 10,
          fontWeight: 600,
          color: statusColor,
          background: statusBg,
          padding: "2px 8px",
          borderRadius: 6,
        }}
      >
        {status}
      </span>
      <span style={{ fontSize: 10, color: C.mutedFg }}>{time}</span>
    </div>
  );
}

function CarePlanRow({
  done,
  label,
  time,
  onAction,
  actionBusy,
}: {
  done: boolean;
  label: string;
  time: string;
  onAction?: () => void;
  actionBusy?: boolean;
}) {
  return (
    <div style={{ ...row, gap: 10, padding: "6px 0" }}>
      {done ? (
        <div
          style={{
            width: 18,
            height: 18,
            borderRadius: 9999,
            background: C.good,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Check size={12} strokeWidth={3} />
        </div>
      ) : (
        <Circle size={18} color={C.mutedFg} />
      )}
      <span style={{ flex: 1, fontSize: 13, color: done ? C.mutedFg : C.fg, textDecoration: done ? "line-through" : "none" }}>
        {label}
      </span>
      {onAction ? (
        <button
          type="button"
          onClick={onAction}
          disabled={Boolean(actionBusy)}
          style={{
            border: `1px solid ${C.primary}`,
            background: "#fff",
            color: C.primary,
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            padding: "3px 8px",
            cursor: actionBusy ? "not-allowed" : "pointer",
            opacity: actionBusy ? 0.7 : 1,
          }}
        >
          {actionBusy ? "Saving..." : "Done"}
        </button>
      ) : (
        <span style={{ fontSize: 11, color: C.mutedFg }}>{time}</span>
      )}
    </div>
  );
}

function TrendCard({
  title,
  value,
  unit,
  color,
  fill,
  points,
  startLabel,
  endLabel,
}: {
  title: string;
  value: string;
  unit?: string;
  color: string;
  fill: string;
  points: number[];
  startLabel?: string;
  endLabel?: string;
}) {
  if (!points || points.length === 0 || points.every(p => p === null || p === undefined || isNaN(p))) {
    return (
      <div
        style={{
          ...col,
          gap: 6,
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: 12,
          minWidth: 180,
          height: 150,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: 12, color: C.mutedFg }}>No data</span>
      </div>
    );
  }

  const validPoints = points.filter(p => typeof p === 'number' && !isNaN(p));
  
  if (validPoints.length === 0) {
    return (
      <div
        style={{
          ...col,
          gap: 6,
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: 12,
          minWidth: 180,
          height: 150,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: 12, color: C.mutedFg }}>No valid data</span>
      </div>
    );
  }

  let displayPoints = [...validPoints];
  if (displayPoints.length < 2) {
    displayPoints = [displayPoints[0], displayPoints[0]];
  }

  const W = 160;
  const H = 60;
  const min = Math.min(...displayPoints);
  const max = Math.max(...displayPoints);
  const range = max - min || 1;
  const step = W / (displayPoints.length - 1);
  const coords = displayPoints.map((p, i) => [
    i * step, 
    H - ((p - min) / range) * (H - 10) - 5
  ]);
  const path = coords.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const area = `${path} L${W},${H} L0,${H} Z`;

  return (
    <div
      style={{
        ...col,
        gap: 6,
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: 12,
        minWidth: 180,
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 600, color: C.fg }}>{title}</span>
      <span style={{ fontSize: 10, color: C.mutedFg }}>7-day trend</span>
      <div style={{ ...row, gap: 4, alignItems: "baseline" }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: C.fg }}>{value}</span>
        {unit ? <span style={{ fontSize: 10, color: C.mutedFg }}>{unit}</span> : null}
      </div>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <path d={area} fill={fill} />
        <path d={path} fill="none" stroke={color} strokeWidth={1.5} />
        {coords.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={2} fill={color} />
        ))}
      </svg>
      <div style={{ ...row, justifyContent: "space-between", fontSize: 9, color: C.mutedFg }}>
        <span>{startLabel || "7 days ago"}</span>
        <span>{endLabel || "Today"}</span>
      </div>
    </div>
  );
}

// Helper functions
function parseBp(raw?: string | null) {
  if (!raw) return null;
  const [s, d] = String(raw).split("/").map((v) => Number(v));
  if (!Number.isFinite(s) || !Number.isFinite(d)) return null;
  return { systolic: s, diastolic: d };
}

function extractBloodPressure(data: any) {
  if (!data) return null;

  const bloodPressure = data.bloodPressure ?? data.bp ?? data.blood_pressure;

  if (Array.isArray(bloodPressure) && bloodPressure.length > 0) {
    const latest = bloodPressure[bloodPressure.length - 1];
    if (latest && typeof latest === 'object' && latest.raw) {
      return latest.raw;
    }
    if (latest && typeof latest === 'object' && latest.systolic != null && latest.diastolic != null) {
      return `${latest.systolic}/${latest.diastolic}`;
    }
    if (typeof latest === 'string') {
      return latest;
    }
    if (latest && typeof latest === 'object' && latest.value) {
      return latest.value;
    }
  }

  if (bloodPressure) {
    if (typeof bloodPressure === 'string') return bloodPressure;
    if (typeof bloodPressure === 'object') {
      if (bloodPressure.raw) return bloodPressure.raw;
      if (bloodPressure.systolic != null && bloodPressure.diastolic != null) {
        return `${bloodPressure.systolic}/${bloodPressure.diastolic}`;
      }
      if (bloodPressure.value) return bloodPressure.value;
    }
  }

  return null;
}

function toNumber(value: any) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function unwrapMetricValue(value: any): string | number | null {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number" || typeof value === "string") return value;

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return unwrapMetricValue(value[value.length - 1]);
  }

  if (typeof value === "object") {
    if (value.raw != null && value.raw !== "") return value.raw;
    if (value.systolic != null && value.diastolic != null) return `${value.systolic}/${value.diastolic}`;
    if (value.value != null) return toNumber(value.value) ?? String(value.value);
    if (Array.isArray(value.values) && value.values.length > 0) {
      return unwrapMetricValue(value.values[value.values.length - 1]);
    }
  }

  return null;
}

function getLatestMetricValue(data: any, field: string) {
  if (!data) return null;

  const key = field;
  const snakeCaseKey = field.replace(/([A-Z])/g, "_$1").toLowerCase();
  const value = data[key] ?? data[snakeCaseKey] ?? null;

  const normalized = unwrapMetricValue(value);
  return normalized ?? null;
}

function getMetricValueFromEntries(entries: any[], field: string) {
  if (!Array.isArray(entries)) return null;

  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    const value = getLatestMetricValue(entry, field);
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }

  return null;
}

function toRenderableValue(value: any): string | number | null {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number" || typeof value === "string") return value;

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return toRenderableValue(value[value.length - 1]);
  }

  if (typeof value === "object") {
    if (value.raw != null && value.raw !== "") return value.raw;
    if (value.systolic != null && value.diastolic != null) return `${value.systolic}/${value.diastolic}`;
    if (value.value != null) return toNumber(value.value) ?? String(value.value);
    if (value._id && value.unit && value.readingType && value.measuredAt) {
      return toNumber(value.value) ?? String(value.value ?? "");
    }
  }

  return String(value);
}

function normalizeSeries(values: Array<number | null | undefined>) {
  const fallback = values.find((v) => typeof v === "number" && !Number.isNaN(v)) ?? 0;
  let prev = fallback;
  return values.map((value) => {
    if (typeof value === "number" && !Number.isNaN(value)) {
      prev = value;
      return value;
    }
    return prev;
  });
}

// Report Options Modal Component
function ReportOptionsModal({ open, onClose, onDownload, duration, setDuration }: any) {
  if (!open) return null;

  const durationOptions = [
    { value: 'day', label: 'Day', icon: '📅', desc: 'Hourly trends for the last 24 hours' },
    { value: 'week', label: 'Week', icon: '📆', desc: 'Daily trends for the last 7 days' },
    { value: 'month', label: 'Month', icon: '📊', desc: 'Daily trends for the last 30 days' },
    { value: 'year', label: 'Year', icon: '📈', desc: 'Monthly trends for the last 12 months' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(8px)',
        zIndex: 1001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: C.bg,
          borderRadius: 24,
          maxWidth: 500,
          width: '100%',
          padding: 32,
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.fg }}>
            <FileText size={24} style={{ display: 'inline', marginRight: 10 }} />
            Download Health Report
          </h3>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              border: 'none',
              background: C.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={16} color={C.fg} />
          </button>
        </div>

        <p style={{ color: C.mutedFg, fontSize: 13, marginBottom: 20 }}>
          Select the time period for your health report
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {durationOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setDuration(option.value as any)}
              style={{
                padding: '16px',
                borderRadius: 12,
                border: duration === option.value ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                background: duration === option.value ? C.primarySoft : C.bg,
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                transition: 'all 0.2s',
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: duration === option.value ? C.primary : C.border,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                }}
              >
                {option.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.fg }}>
                  {option.label}
                </div>
                <div style={{ fontSize: 12, color: C.mutedFg }}>
                  {option.desc}
                </div>
              </div>
              {duration === option.value && (
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    background: C.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Check size={14} color="#fff" />
                </div>
              )}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              background: C.bg,
              color: C.fg,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onDownload}
            style={{
              flex: 2,
              padding: '12px',
              borderRadius: 12,
              border: 'none',
              background: C.primary,
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <FileText size={16} /> Download Report
          </button>
        </div>
      </div>
    </div>
  );
}

// Trends Modal Component
function TrendsModal({ open, onClose, recentVitals }: any) {
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month' | 'year'>('week');

  if (!open) return null;

  const getDateRange = () => {
    const now = new Date();
    const end = new Date(now);
    let start = new Date(now);

    switch (timeRange) {
      case 'day':
        start.setHours(0, 0, 0, 0);
        break;
      case 'week':
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        start.setFullYear(now.getFullYear() - 1);
        break;
      default:
        start.setDate(now.getDate() - 7);
    }

    return { start, end };
  };

  const filterVitalsByDateRange = (vitals: any[]) => {
    const { start, end } = getDateRange();
    return vitals.filter((v) => {
      const date = new Date(v.createdAt);
      return date >= start && date <= end;
    });
  };

  const getTimeLabels = () => {
    const { start, end } = getDateRange();
    const format = (date: Date) => {
      if (timeRange === 'day') {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      if (timeRange === 'week') {
        return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
      }
      if (timeRange === 'month') {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
      return date.toLocaleDateString([], { month: 'short', year: 'numeric' });
    };
    return { startLabel: format(start), endLabel: format(end) };
  };

  const getTrendDataForRange = () => {
    const filteredVitals = filterVitalsByDateRange(recentVitals);
    const { startLabel, endLabel } = getTimeLabels();

    const groupedData = new Map();

    filteredVitals.forEach((entry) => {
      const date = new Date(entry.createdAt);
      let key;
      if (timeRange === 'day') {
        key = date.toISOString().slice(0, 16);
      } else if (timeRange === 'week') {
        key = date.toISOString().slice(0, 10);
      } else if (timeRange === 'month') {
        key = date.toISOString().slice(0, 10);
      } else {
        key = date.toISOString().slice(0, 7);
      }

      if (!groupedData.has(key)) {
        groupedData.set(key, []);
      }
      groupedData.get(key).push(entry);
    });

    const sortedKeys = Array.from(groupedData.keys()).sort();

    const calculateAverage = (entries: any[], field: string, extractFn?: (entry: any) => number | null) => {
      const values: number[] = [];
      entries.forEach((entry) => {
        let value = null;
        if (extractFn) {
          value = extractFn(entry);
        } else if (Array.isArray(entry[field]) && entry[field].length > 0) {
          const latest = entry[field][entry[field].length - 1];
          value = unwrapMetricValue(latest);
        } else if (entry[field] !== undefined && entry[field] !== null) {
          value = unwrapMetricValue(entry[field]);
        }
        if (value !== null && typeof value === 'number' && !Number.isNaN(value)) {
          values.push(value);
        }
      });
      if (values.length === 0) return null;
      return values.reduce((sum, v) => sum + v, 0) / values.length;
    };

    const bpValues = sortedKeys.map((key) => {
      const entries = groupedData.get(key);
      return calculateAverage(entries, 'bloodPressure', (entry: any) => {
        const bp = extractBloodPressure(entry);
        if (bp) {
          const parsed = parseBp(bp);
          if (parsed) return parsed.systolic;
        }
        return null;
      });
    });

    const sugarValues = sortedKeys.map((key) => {
      const entries = groupedData.get(key);
      return calculateAverage(entries, 'bloodSugar');
    });

    const hrValues = sortedKeys.map((key) => {
      const entries = groupedData.get(key);
      return calculateAverage(entries, 'heartRate');
    });

    const tempValues = sortedKeys.map((key) => {
      const entries = groupedData.get(key);
      return calculateAverage(entries, 'temperature');
    });

    const weightValues = sortedKeys.map((key) => {
      const entries = groupedData.get(key);
      return calculateAverage(entries, 'weight');
    });

    const adherenceValues = sortedKeys.map((key) => {
      const entries = groupedData.get(key);
      const allEvents: any[] = [];
      entries.forEach((entry: any) => {
        if (Array.isArray(entry.adherenceEvents)) {
          allEvents.push(...entry.adherenceEvents);
        }
      });
      if (allEvents.length === 0) return null;
      const takenCount = allEvents.filter((event) => 
        String(event?.status || "").toLowerCase() === "taken"
      ).length;
      return (takenCount / allEvents.length) * 100;
    });

    const latest = filteredVitals[filteredVitals.length - 1] || {};
    
    let latestSugar = null;
    if (Array.isArray(latest.bloodSugar) && latest.bloodSugar.length > 0) {
      const sugar = latest.bloodSugar[latest.bloodSugar.length - 1];
      latestSugar = unwrapMetricValue(sugar);
    }

    const latestBpRaw = extractBloodPressure(latest) || extractBloodPressure(recentVitals[0]);
    
    let latestHeartRate = null;
    if (Array.isArray(latest.heartRate) && latest.heartRate.length > 0) {
      const hr = latest.heartRate[latest.heartRate.length - 1];
      latestHeartRate = unwrapMetricValue(hr);
    }

    let latestTemperature = null;
    if (Array.isArray(latest.temperature) && latest.temperature.length > 0) {
      const temp = latest.temperature[latest.temperature.length - 1];
      latestTemperature = unwrapMetricValue(temp);
    }

    let latestWeight = null;
    if (Array.isArray(latest.weight) && latest.weight.length > 0) {
      const w = latest.weight[latest.weight.length - 1];
      latestWeight = unwrapMetricValue(w);
    }

    const trends = [];

    if (bpValues.some(v => v !== null)) {
      trends.push({
        key: "bp-trend",
        title: "Blood Pressure",
        value: latestBpRaw || "--",
        unit: latestBpRaw ? "mmHg" : undefined,
        color: C.pinkFg,
        fill: "#fee2e230",
        points: normalizeSeries(bpValues),
        startLabel,
        endLabel,
      });
    }

    if (hrValues.some(v => v !== null)) {
      trends.push({
        key: "hr-trend",
        title: "Heart Rate",
        value: latestHeartRate != null ? String(latestHeartRate) : "--",
        unit: latestHeartRate != null ? "bpm" : undefined,
        color: C.blueFg,
        fill: "#dbeafe60",
        points: normalizeSeries(hrValues),
        startLabel,
        endLabel,
      });
    }

    if (sugarValues.some(v => v !== null)) {
      trends.push({
        key: "sugar-trend",
        title: "Blood Sugar",
        value: latestSugar != null ? String(latestSugar) : "--",
        unit: latestSugar != null ? "mg/dL" : undefined,
        color: C.primary,
        fill: "#f1e6ff60",
        points: normalizeSeries(sugarValues),
        startLabel,
        endLabel,
      });
    }

    if (tempValues.some(v => v !== null)) {
      trends.push({
        key: "temp-trend",
        title: "Temperature",
        value: latestTemperature != null ? String(latestTemperature) : "--",
        unit: latestTemperature != null ? "°F" : undefined,
        color: C.peachFg,
        fill: "#ffedd560",
        points: normalizeSeries(tempValues),
        startLabel,
        endLabel,
      });
    }

    if (weightValues.some(v => v !== null)) {
      trends.push({
        key: "weight-trend",
        title: "Weight",
        value: latestWeight != null ? String(latestWeight) : "--",
        unit: latestWeight != null ? "kg" : undefined,
        color: C.good,
        fill: "#dcfce760",
        points: normalizeSeries(weightValues),
        startLabel,
        endLabel,
      });
    }

    if (adherenceValues.some(v => v !== null)) {
      trends.push({
        key: "meds-trend",
        title: "Medication Adherence",
        value: adherenceValues[adherenceValues.length - 1] != null ? 
          String(Math.round(adherenceValues[adherenceValues.length - 1]!)) : "--",
        unit: "%",
        color: C.warn,
        fill: "#fef3c760",
        points: normalizeSeries(adherenceValues),
        startLabel,
        endLabel,
      });
    }

    return trends;
  };

  const getTrendInsight = (points: number[], label: string) => {
    if (points.length < 2) return { direction: 'neutral', message: 'Not enough data to determine trend.' };
    
    const first = points[0];
    const last = points[points.length - 1];
    const diff = last - first;
    const percentChange = first !== 0 ? ((diff / first) * 100) : 0;

    let direction = 'neutral';
    let message = '';

    if (Math.abs(diff) < 0.5) {
      direction = 'neutral';
      message = `${label} has been stable over the selected period.`;
    } else if (diff > 0) {
      direction = 'up';
      message = `${label} has increased by ${Math.abs(percentChange).toFixed(1)}% over the selected period.`;
    } else {
      direction = 'down';
      message = `${label} has decreased by ${Math.abs(percentChange).toFixed(1)}% over the selected period.`;
    }

    if (label === 'Blood Pressure') {
      if (last > 140) message += ' This is above the normal range (120/80). Consider consulting your doctor.';
      else if (last < 90) message += ' This is below the normal range. Monitor for symptoms of low blood pressure.';
      else message += ' This is within a healthy range. Keep up the good work!';
    } else if (label === 'Blood Sugar') {
      if (last > 140) message += ' This is above the normal range (70-140 mg/dL). Monitor your diet and consult your doctor.';
      else if (last < 70) message += ' This is below the normal range. Consider eating something to raise your blood sugar.';
      else message += ' This is within a healthy range. Great job managing your blood sugar!';
    } else if (label === 'Heart Rate') {
      if (last > 100) message += ' This is above the normal resting range (55-100 bpm). Consider relaxation techniques.';
      else if (last < 55) message += ' This is below the normal resting range. Monitor for symptoms of bradycardia.';
      else message += ' This is within a healthy range. Your heart rate looks good!';
    } else if (label === 'Temperature') {
      if (last > 100.4) message += ' This indicates a fever. Stay hydrated and monitor your symptoms.';
      else if (last < 95) message += ' This is below normal body temperature. Stay warm and monitor for hypothermia symptoms.';
      else message += ' This is within a healthy range. Your temperature is normal!';
    } else if (label === 'Weight') {
      if (diff > 2) message += ' You\'ve gained weight. Consider reviewing your diet and exercise routine.';
      else if (diff < -2) message += ' You\'ve lost weight. Ensure you\'re eating enough and staying healthy.';
      else message += ' Your weight has been stable. Keep maintaining a healthy lifestyle!';
    }

    return { direction, message };
  };

  const getTrendIcon = (direction: string) => {
    if (direction === 'up') return <TrendingUp size={16} color={C.pinkFg} />;
    if (direction === 'down') return <TrendingDown size={16} color={C.blueFg} />;
    return <Minus size={16} color={C.mutedFg} />;
  };

  const getMetricIcon = (title: string) => {
    if (title.includes('Blood Pressure')) return <HeartPulse size={20} color={C.pinkFg} />;
    if (title.includes('Heart Rate')) return <Heart size={20} color={C.pinkFg} />;
    if (title.includes('Blood Sugar')) return <Droplet size={20} color={C.blueFg} />;
    if (title.includes('Temperature')) return <Thermometer size={20} color={C.peachFg} />;
    if (title.includes('Weight')) return <Scale size={20} color={C.good} />;
    if (title.includes('Medication')) return <Pill size={20} color={C.primary} />;
    return <TrendingUp size={20} color={C.primary} />;
  };

  const getMetricColor = (title: string) => {
    if (title.includes('Blood Pressure')) return { bg: C.pink, fg: C.pinkFg };
    if (title.includes('Heart Rate')) return { bg: C.pink, fg: C.pinkFg };
    if (title.includes('Blood Sugar')) return { bg: C.blue, fg: C.blueFg };
    if (title.includes('Temperature')) return { bg: C.peach, fg: C.peachFg };
    if (title.includes('Weight')) return { bg: C.goodSoft, fg: C.good };
    if (title.includes('Medication')) return { bg: C.primarySoft, fg: C.primary };
    return { bg: C.primarySoft, fg: C.primary };
  };

  const timeRangeOptions = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
  ];

  const displayedTrends = getTrendDataForRange();

  const ModalTrendCard = ({ title, value, unit, color, fill, points, startLabel, endLabel }: any) => {
    if (!points || points.length === 0 || points.every(p => p === null || p === undefined || isNaN(p))) {
      return (
        <div style={{ 
          width: '100%', 
          height: 100, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: C.border,
          borderRadius: 8,
          color: C.mutedFg,
          fontSize: 13
        }}>
          No data available for this metric
        </div>
      );
    }

    const validPoints = points.filter(p => typeof p === 'number' && !isNaN(p));
    
    if (validPoints.length === 0) {
      return (
        <div style={{ 
          width: '100%', 
          height: 100, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: C.border,
          borderRadius: 8,
          color: C.mutedFg,
          fontSize: 13
        }}>
          No valid data points
        </div>
      );
    }

    let displayPoints = [...validPoints];
    if (displayPoints.length < 2) {
      displayPoints = [displayPoints[0], displayPoints[0]];
    }

    const W = 500;
    const H = 100;
    const min = Math.min(...displayPoints);
    const max = Math.max(...displayPoints);
    const range = max - min || 1;
    const step = W / (displayPoints.length - 1);
    const coords = displayPoints.map((p, i) => [
      i * step, 
      H - ((p - min) / range) * (H - 20) - 10
    ]);
    
    const path = coords.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
    const area = `${path} L${W},${H} L0,${H} Z`;

    return (
      <div style={{ width: '100%' }}>
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
          <path d={area} fill={fill} />
          <path d={path} fill="none" stroke={color} strokeWidth={2.5} />
          {coords.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={4} fill={color} />
          ))}
          <g style={{ fontSize: 10, fill: C.mutedFg }}>
            {startLabel && <text x={0} y={H - 2}>{startLabel}</text>}
            {endLabel && <text x={W - 40} y={H - 2}>{endLabel}</text>}
          </g>
        </svg>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(8px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '20px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: C.bg,
          borderRadius: 24,
          maxWidth: 900,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: 24,
          position: 'relative',
          marginTop: 20,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: C.fg, margin: 0 }}>Health Trends</h2>
            <p style={{ fontSize: 13, color: C.mutedFg, margin: '4px 0 0' }}>
              Track your health metrics over time
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 40,
              height: 40,
              borderRadius: 999,
              border: `1px solid ${C.border}`,
              background: C.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={20} color={C.fg} />
          </button>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {timeRangeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setTimeRange(option.value as any)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 999,
                  border: timeRange === option.value ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                  background: timeRange === option.value ? C.primarySoft : C.bg,
                  color: timeRange === option.value ? C.primary : C.mutedFg,
                  fontSize: 13,
                  fontWeight: timeRange === option.value ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: C.mutedFg }}>
            <Calendar size={14} style={{ display: 'inline', marginRight: 4 }} />
            Showing data for: {getDateRange().start.toLocaleDateString()} - {getDateRange().end.toLocaleDateString()}
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 12,
            marginBottom: 24,
          }}
        >
          {displayedTrends.slice(0, 3).map((trend: any) => {
            const insight = getTrendInsight(trend.points, trend.title);
            const colors = getMetricColor(trend.title);
            return (
              <div
                key={`summary-${trend.key}`}
                style={{
                  background: colors.bg,
                  borderRadius: 12,
                  padding: 12,
                  border: `1px solid ${C.border}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  {getMetricIcon(trend.title)}
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.mutedFg }}>{trend.title}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: C.fg }}>{trend.value}</span>
                  {getTrendIcon(insight.direction)}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {displayedTrends.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: C.mutedFg }}>
              <p style={{ fontSize: 16 }}>No data available for the selected time range.</p>
              <p style={{ fontSize: 13 }}>Try selecting a different time range or log more vitals.</p>
            </div>
          ) : (
            displayedTrends.map((trend: any) => {
              const insight = getTrendInsight(trend.points, trend.title);
              const colors = getMetricColor(trend.title);
              
              return (
                <div
                  key={trend.key}
                  style={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 16,
                    padding: 16,
                    background: C.bg,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: colors.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {getMetricIcon(trend.title)}
                      </div>
                      <div>
                        <h4 style={{ fontSize: 15, fontWeight: 600, color: C.fg, margin: 0 }}>{trend.title}</h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <span style={{ fontSize: 13, color: C.mutedFg }}>Current: {trend.value}</span>
                          <span style={{ fontSize: 11, color: C.mutedFg }}>•</span>
                          <span style={{ fontSize: 11, color: C.mutedFg }}>
                            {timeRange === 'day' ? 'Hourly' : timeRange === 'week' ? 'Daily' : timeRange === 'month' ? 'Daily' : 'Monthly'} trend
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {getTrendIcon(insight.direction)}
                      <span style={{ fontSize: 12, color: C.mutedFg }}>
                        {insight.direction === 'up' ? 'Increasing' : insight.direction === 'down' ? 'Decreasing' : 'Stable'}
                      </span>
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <ModalTrendCard
                      title=""
                      value=""
                      unit={trend.unit}
                      color={trend.color}
                      fill={trend.fill}
                      points={trend.points}
                      startLabel={trend.startLabel}
                      endLabel={trend.endLabel}
                    />
                  </div>

                  <div
                    style={{
                      background: colors.bg,
                      borderRadius: 10,
                      padding: 12,
                      border: `1px solid ${colors.bg}`,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                    }}
                  >
                    <div style={{ marginTop: 2 }}>{getTrendIcon(insight.direction)}</div>
                    <p style={{ fontSize: 13, color: C.fg, margin: 0, lineHeight: 1.5 }}>
                      {insight.message}
                    </p>
                  </div>

                  <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {trend.points.map((point: number, index: number) => (
                      <div
                        key={`point-${index}`}
                        style={{
                          background: C.primarySoft,
                          borderRadius: 6,
                          padding: '4px 8px',
                          fontSize: 11,
                          color: C.fg,
                        }}
                      >
                        {timeRange === 'day' ? `Hour ${index + 1}` : 
                         timeRange === 'week' ? `Day ${index + 1}` : 
                         timeRange === 'month' ? `Day ${index + 1}` : 
                         `Month ${index + 1}`}: {point}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {displayedTrends.length > 0 && (
          <div
            style={{
              marginTop: 24,
              padding: 16,
              background: C.primarySoft,
              borderRadius: 16,
              border: `1px solid ${C.primary}`,
            }}
          >
            <h4 style={{ fontSize: 15, fontWeight: 600, color: C.fg, margin: '0 0 8px' }}>
              💡 Health Recommendations
            </h4>
            <ul style={{ margin: 0, paddingLeft: 20, color: C.fg, fontSize: 13, lineHeight: 1.8 }}>
              {displayedTrends.some((t: any) => t.title.includes('Blood Pressure') && parseFloat(t.value) > 140) && (
                <li>Your blood pressure is elevated. Consider reducing salt intake and increasing physical activity.</li>
              )}
              {displayedTrends.some((t: any) => t.title.includes('Blood Sugar') && parseFloat(t.value) > 140) && (
                <li>Your blood sugar is high. Monitor your carbohydrate intake and consult your doctor.</li>
              )}
              {displayedTrends.some((t: any) => t.title.includes('Heart Rate') && parseFloat(t.value) > 100) && (
                <li>Your heart rate is elevated. Practice deep breathing exercises and reduce stress.</li>
              )}
              {displayedTrends.some((t: any) => t.title.includes('Temperature') && parseFloat(t.value) > 100.4) && (
                <li>You have a fever. Rest, stay hydrated, and consult your doctor if symptoms persist.</li>
              )}
              {displayedTrends.every((t: any) => {
                const val = parseFloat(t.value);
                return !isNaN(val) && (
                  (t.title.includes('Blood Pressure') && val < 130) ||
                  (t.title.includes('Blood Sugar') && val >= 70 && val <= 140) ||
                  (t.title.includes('Heart Rate') && val >= 55 && val <= 100) ||
                  (t.title.includes('Temperature') && val >= 95 && val <= 99.5)
                );
              }) && (
                <li>All your vitals are within normal ranges! Keep up the good health habits! 🎉</li>
              )}
            </ul>
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%',
            marginTop: 16,
            padding: '12px',
            borderRadius: 12,
            border: 'none',
            background: C.primary,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Close Trends
        </button>
      </div>
    </div>
  );
}

function EmergencyModal({ open, onClose, alert, onDismiss }: any) {
  if (!open || !alert) return null;

  const handleCallDoctor = () => {
    window.location.href = "tel:";
    onDismiss();
  };

  const handleDismiss = () => {
    onDismiss();
  };

  const renderVital = (label: string, value: any, unit: string) => {
    if (value === null || value === undefined || value === "") return null;
    const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
    return (
      <div style={{ fontSize: 12 }}>
        <span style={{ color: C.mutedFg }}>{label}: </span>
        <span style={{ fontWeight: 600, color: C.fg }}>{displayValue} {unit}</span>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onDismiss}
    >
      <div
        style={{
          background: C.bg,
          borderRadius: 24,
          maxWidth: 500,
          width: '100%',
          padding: 32,
          position: 'relative',
          border: `3px solid ${alert.level === "critical" ? C.danger : C.warn}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: alert.level === "critical" ? C.danger : C.warn }}>
            <Siren size={24} style={{ display: 'inline', marginRight: 10 }} />
            {alert.level === "critical" ? "EMERGENCY" : "Health Warning"}
          </h3>
          <button
            onClick={handleDismiss}
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              border: 'none',
              background: C.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={16} color={C.fg} />
          </button>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {alert.conditions.map((condition: string, index: number) => (
              <span
                key={index}
                style={{
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: alert.level === "critical" ? C.dangerSoft : C.warnSoft,
                  color: alert.level === "critical" ? C.danger : C.warn,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {condition}
              </span>
            ))}
          </div>
          <p style={{ color: C.fg, fontSize: 14, lineHeight: 1.6, margin: '0 0 16px' }}>
            {alert.message}
          </p>

          {alert.vitals && (
            <div style={{ background: C.shellBg, borderRadius: 12, padding: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.mutedFg, display: 'block', marginBottom: 8 }}>
                Current Vitals
              </span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {renderVital("BP", alert.vitals.bp, "mmHg")}
                {renderVital("HR", alert.vitals.heartRate, "bpm")}
                {renderVital("Sugar", alert.vitals.sugar, "mg/dL")}
                {renderVital("Temp", alert.vitals.temperature, "°F")}
                {renderVital("O2", alert.vitals.oxygenLevel, "%")}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {alert.level === "critical" && (
            <button
              onClick={handleCallDoctor}
              style={{
                padding: '14px',
                borderRadius: 12,
                border: 'none',
                background: C.danger,
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Phone size={18} /> Call Emergency / Doctor
            </button>
          )}
          <button
            onClick={handleDismiss}
            style={{
              padding: '14px',
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              background: C.bg,
              color: C.fg,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Dismiss Alert
          </button>
        </div>

        <p style={{ fontSize: 11, color: C.mutedFg, textAlign: 'center', marginTop: 16, margin: '16px 0 0' }}>
          If this is a medical emergency, please call your local emergency services immediately.
        </p>
      </div>
    </div>
  );
}

export default function RemoteMonitoring() {
  const { addNotification } = useNotifications() as any;
  const navigate = useNavigate();
  const trustStripRef = useRef<HTMLDivElement | null>(null);
  const isTrustStripPausedRef = useRef(false);
  const carePlanQueueTimerRef = useRef<number | null>(null);
  const carePlanNotifiedRef = useRef<Set<string>>(new Set());
  const carePlanSignatureRef = useRef<string>("");
  const [metricsDashboard, setMetricsDashboard] = useState<any>(null);
  const [recentVitals, setRecentVitals] = useState<any[]>([]);
  const [vitalsLoading, setVitalsLoading] = useState(true);
  const [vitalsError, setVitalsError] = useState("");
  const [todayMedicationReminders, setTodayMedicationReminders] = useState<any[]>([]);
  const [todayAdditionalReminders, setTodayAdditionalReminders] = useState<any[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(true);
  const [remindersError, setRemindersError] = useState("");
  const [healthPlan, setHealthPlan] = useState<any>(null);
  const [healthPlanLoading, setHealthPlanLoading] = useState(true);
  const [healthPlanError, setHealthPlanError] = useState("");
  const [healthPlanActionKey, setHealthPlanActionKey] = useState("");
  const [showAddMedicationModal, setShowAddMedicationModal] = useState(false);
  const [showTrendsModal, setShowTrendsModal] = useState(false);
  const [showReportOptions, setShowReportOptions] = useState(false);
  const [reportDuration, setReportDuration] = useState<'day' | 'week' | 'month' | 'year'>('week');
  const [medications, setMedications] = useState<any[]>([]);
  const [medicationsLoading, setMedicationsLoading] = useState(true);
  const [medicationsError, setMedicationsError] = useState("");
  const [showMedicationModal, setShowMedicationModal] = useState(false);
  const [editingMedication, setEditingMedication] = useState<any>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [deletingMedicationId, setDeletingMedicationId] = useState<string | null>(null);
  const [emergencyAlert, setEmergencyAlert] = useState<{
    active: boolean;
    level: "critical" | "warning";
    conditions: string[];
    message: string;
    vitals: any;
    timestamp: Date;
  } | null>(null);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const lastEmergencyKeyRef = useRef<string>("");

  const shortDateLabel = (value?: Date | string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const formatTimeLabel = (value?: string | Date | null) => {
    if (!value) return "No reading yet";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "No reading yet";
    const day = date.toDateString() === new Date().toDateString() ? "Today" : date.toLocaleDateString();
    return `${day}, ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  };

  const isWithinRange = (value: number | null | undefined, min: number, max: number) => {
    if (typeof value !== "number" || Number.isNaN(value)) return false;
    return value >= min && value <= max;
  };

  const fetchMedications = async () => {
    try {
      setMedicationsLoading(true);
      const response = await api.get("/api/medications");
      console.log(response.data.medications, "medications");
      setMedications(response?.data?.medications || []);
    } catch (error: any) {
      console.error("Error fetching medications:", error);
      setMedicationsError(error?.response?.data?.message || "Unable to load medications");
    } finally {
      setMedicationsLoading(false);
    }
  };

  const handleAddMedication = async (medicationData: any) => {
    try {
      const response = await api.post("/api/medications", medicationData);
      setMedications([...medications, response.data.medication]);
      setShowMedicationModal(false);
      emitHealthDataUpdated("medication:added");
      if (addNotification) {
        addNotification({
          type: 'success',
          title: 'Medication Added',
          body: `${medicationData.medicineName} has been added to your medications.`,
          icon: '💊',
        });
      }
    } catch (error: any) {
      console.error("Error adding medication:", error);
      if (addNotification) {
        addNotification({
          type: 'error',
          title: 'Failed to Add',
          body: error?.response?.data?.message || 'Unable to add medication. Please try again.',
          icon: '❌',
        });
      }
    }
  };

  const handleUpdateMedication = async (medicationData: any) => {
    try {
      const response = await api.put(`/api/medications/${editingMedication._id}`, medicationData);
      setMedications(medications.map(m => 
        m._id === editingMedication._id ? response.data.medication : m
      ));
      setShowMedicationModal(false);
      setEditingMedication(null);
      emitHealthDataUpdated("medication:updated");
      if (addNotification) {
        addNotification({
          type: 'success',
          title: 'Medication Updated',
          body: `${medicationData.medicineName} has been updated.`,
          icon: '💊',
        });
      }
    } catch (error: any) {
      console.error("Error updating medication:", error);
      if (addNotification) {
        addNotification({
          type: 'error',
          title: 'Failed to Update',
          body: error?.response?.data?.message || 'Unable to update medication. Please try again.',
          icon: '❌',
        });
      }
    }
  };

  const handleDeleteMedication = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this medication?')) return;
    
    try {
      setDeletingMedicationId(id);
      await api.delete(`/api/medications/${id}`);
      setMedications(medications.filter(m => m._id !== id));
      emitHealthDataUpdated("medication:deleted");
      if (addNotification) {
        addNotification({
          type: 'success',
          title: 'Medication Deleted',
          body: 'The medication has been removed from your list.',
          icon: '🗑️',
        });
      }
    } catch (error: any) {
      console.error("Error deleting medication:", error);
      if (addNotification) {
        addNotification({
          type: 'error',
          title: 'Failed to Delete',
          body: error?.response?.data?.message || 'Unable to delete medication. Please try again.',
          icon: '❌',
        });
      }
    } finally {
      setDeletingMedicationId(null);
    }
  };

  const openAddModal = () => {
    setEditingMedication(null);
    setModalMode('add');
    setShowMedicationModal(true);
  };

  const openEditModal = (medication: any) => {
    setEditingMedication(medication);
    setModalMode('edit');
    setShowMedicationModal(true);
  };

  const getReportDateRange = () => {
    const now = new Date();
    const end = new Date(now);
    let start = new Date(now);

    switch (reportDuration) {
      case 'day':
        start.setHours(0, 0, 0, 0);
        break;
      case 'week':
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        start.setFullYear(now.getFullYear() - 1);
        break;
      default:
        start.setDate(now.getDate() - 7);
    }

    return { start, end };
  };

  const downloadHealthReport = async () => {
    try {
      const { start, end } = getReportDateRange();
      
      const [dashboardRes, vitalsRes] = await Promise.all([
        api.get("/api/metrics/dashboard"),
        api.get("/api/vitals", { params: { limit: 500, skip: 0 } }),
      ]);

      console.log("dashboardRes", dashboardRes);

      const dashboard = dashboardRes?.data?.metrics || {};
      const allVitals = Array.isArray(vitalsRes?.data?.vitals) ? vitalsRes.data.vitals : [];
      
      const vitals = allVitals.filter(v => {
        const date = new Date(v.createdAt);
        return date >= start && date <= end;
      });
      const reportVitals = [...vitals].sort(
        (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
      );

      const latest = dashboard?.vitals?.latest || {};

      const now = new Date();
      const dateStr = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const timeStr = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      const durationLabels = {
        day: 'Daily',
        week: 'Weekly',
        month: 'Monthly',
        year: 'Yearly'
      };

      const latestReportVital = reportVitals[0] || null;
      const getReportMetric = (field: string) =>
        getLatestMetricValue(latestReportVital, field) ?? getLatestMetricValue(latest, field);

      const bpRaw = extractBloodPressure(latestReportVital) || extractBloodPressure(latest);
      const heartRate = getReportMetric("heartRate");
      const temperature = getReportMetric("temperature");
      const weight = getReportMetric("weight");
      const sugarValue = getReportMetric("bloodSugar");

      console.log(dashboard, "dashboard")

      const medsSummary = dashboard?.medications || {};
      const medsTaken = typeof medsSummary?.todayTaken === "number" ? medsSummary.todayTaken : 0;
      const medsScheduled = typeof medsSummary?.todayScheduled === "number" ? medsSummary.todayScheduled : 0;

      const groupedData = new Map();
      vitals.forEach(entry => {
        const date = new Date(entry.createdAt);
        let key;
        if (reportDuration === 'day') {
          key = date.toISOString().slice(0, 13);
        } else if (reportDuration === 'week' || reportDuration === 'month') {
          key = date.toISOString().slice(0, 10);
        } else {
          key = date.toISOString().slice(0, 7);
        }
        if (!groupedData.has(key)) {
          groupedData.set(key, []);
        }
        groupedData.get(key).push(entry);
      });

      const sortedKeys = Array.from(groupedData.keys()).sort();

      const trends = sortedKeys.map(key => {
        const entries = groupedData.get(key) || [];
        let bpAvg = null;
        let sugarAvg = null;
        let hrAvg = null;
        let tempAvg = null;
        let weightAvg = null;
        let label = key;

        if (reportDuration === 'day') {
          const date = new Date(key + ':00');
          label = date.toLocaleTimeString([], { hour: '2-digit' });
        } else if (reportDuration === 'week' || reportDuration === 'month') {
          const date = new Date(key);
          label = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        } else {
          const date = new Date(key + '-01');
          label = date.toLocaleDateString([], { month: 'short', year: 'numeric' });
        }

        if (entries.length > 0) {
          const bpValues = [];
          const sugarValues = [];
          const hrValues = [];
          const tempValues = [];
          const weightValues = [];

          entries.forEach(entry => {
            const bp = extractBloodPressure(entry);
            if (bp) {
              const parsed = parseBp(bp);
              if (parsed) bpValues.push(parsed.systolic);
            }
            const sugar = toNumber(getLatestMetricValue(entry, "bloodSugar"));
            const heartRate = toNumber(getLatestMetricValue(entry, "heartRate"));
            const temperature = toNumber(getLatestMetricValue(entry, "temperature"));
            const weight = toNumber(getLatestMetricValue(entry, "weight"));
            if (sugar != null) sugarValues.push(sugar);
            if (heartRate != null) hrValues.push(heartRate);
            if (temperature != null) tempValues.push(temperature);
            if (weight != null) weightValues.push(weight);
          });

          if (bpValues.length > 0) bpAvg = Math.round(bpValues.reduce((a, b) => a + b, 0) / bpValues.length);
          if (sugarValues.length > 0) sugarAvg = Math.round(sugarValues.reduce((a, b) => a + b, 0) / sugarValues.length);
          if (hrValues.length > 0) hrAvg = Math.round(hrValues.reduce((a, b) => a + b, 0) / hrValues.length);
          if (tempValues.length > 0) tempAvg = Math.round((tempValues.reduce((a, b) => a + b, 0) / tempValues.length) * 10) / 10;
          if (weightValues.length > 0) weightAvg = Math.round((weightValues.reduce((a, b) => a + b, 0) / weightValues.length) * 10) / 10;
        }

        return { label, bpAvg, sugarAvg, hrAvg, tempAvg, weightAvg };
      });

      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Health Report - ${dateStr}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 0;
      padding: 40px;
      background: #f8fafc;
      color: #0f172a;
    }
    .container {
      max-width: 900px;
      margin: 0 auto;
      background: #ffffff;
      border-radius: 16px;
      padding: 40px;
      box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
    }
    .header {
      text-align: center;
      border-bottom: 2px solid #f1e6ff;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    .header h1 {
      margin: 0;
      color: #7f00ff;
      font-size: 28px;
    }
    .header p {
      margin: 5px 0 0;
      color: #6b7a82;
      font-size: 14px;
    }
    .header .duration-badge {
      display: inline-block;
      background: #f1e6ff;
      color: #7f00ff;
      padding: 4px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
      margin-top: 8px;
    }
    .section {
      margin-bottom: 24px;
    }
    .section-title {
      font-size: 18px;
      font-weight: 600;
      color: #0f172a;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid #eceef1;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 12px;
      margin-bottom: 16px;
    }
    .card {
      background: #f8fafc;
      padding: 12px;
      border-radius: 12px;
      border: 1px solid #eceef1;
    }
    .card-label {
      font-size: 12px;
      color: #6b7a82;
      font-weight: 500;
    }
    .card-value {
      font-size: 20px;
      font-weight: 700;
      color: #0f172a;
      margin-top: 4px;
    }
    .card-status {
      font-size: 11px;
      margin-top: 4px;
      padding: 2px 8px;
      border-radius: 6px;
      display: inline-block;
    }
    .status-normal { background: #dcfce7; color: #16a34a; }
    .status-warning { background: #fef3c7; color: #d97706; }
    .status-danger { background: #fee2e2; color: #dc2626; }
    .trend-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    .trend-table th {
      background: #f1e6ff;
      color: #7f00ff;
      padding: 10px;
      text-align: left;
      font-weight: 600;
    }
    .trend-table td {
      padding: 8px 10px;
      border-bottom: 1px solid #eceef1;
    }
    .trend-table tr:last-child td {
      border-bottom: none;
    }
    .trend-table tr:hover {
      background: #faf5ff;
    }
    .no-data {
      text-align: center;
      color: #6b7a82;
      padding: 20px;
    }
    .insight {
      background: #f1e6ff;
      padding: 16px;
      border-radius: 12px;
      margin-top: 16px;
    }
    .insight h4 {
      margin: 0 0 8px;
      color: #7f00ff;
    }
    .insight ul {
      margin: 0;
      padding-left: 20px;
      color: #1f2937;
      font-size: 13px;
      line-height: 1.8;
    }
    .stats-row {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      padding: 12px;
      background: #f8fafc;
      border-radius: 12px;
      margin-bottom: 16px;
    }
    .stats-row .stat {
      display: flex;
      flex-direction: column;
    }
    .stats-row .stat .label {
      font-size: 11px;
      color: #6b7a82;
    }
    .stats-row .stat .value {
      font-size: 14px;
      font-weight: 600;
      color: #0f172a;
    }
    .footer {
      text-align: center;
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid #eceef1;
      color: #6b7a82;
      font-size: 12px;
    }
    .highlight {
      color: #7f00ff;
      font-weight: 600;
    }
    @media print {
      body { background: #fff; padding: 20px; }
      .container { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>📊 Health Report</h1>
      <p>Generated on ${dateStr} at ${timeStr}</p>
      <span class="duration-badge">${durationLabels[reportDuration]} Report</span>
    </div>

    <div class="stats-row">
      <div class="stat">
        <span class="label">Report Period</span>
        <span class="value">${start.toLocaleDateString()} - ${end.toLocaleDateString()}</span>
      </div>
      <div class="stat">
        <span class="label">Total Readings</span>
        <span class="value">${vitals.length}</span>
      </div>
      <div class="stat">
        <span class="label">Data Points</span>
        <span class="value">${trends.length} ${reportDuration === 'day' ? 'hours' : reportDuration === 'week' || reportDuration === 'month' ? 'days' : 'months'}</span>
      </div>
    </div>

    <div class="section">
      <div class="section-title">📋 Current Summary</div>
      <div class="grid">
        <div class="card">
          <div class="card-label">Blood Pressure</div>
          <div class="card-value">${bpRaw || '--'}</div>
          ${bpRaw ? `<span class="card-status ${bpRaw.includes('130') ? 'status-warning' : 'status-normal'}">${bpRaw.includes('130') ? 'Monitor' : 'Normal'}</span>` : ''}
        </div>
        <div class="card">
          <div class="card-label">Heart Rate</div>
          <div class="card-value">${heartRate != null ? heartRate : '--'} <span style="font-size:14px;font-weight:400;color:#6b7a82;">bpm</span></div>
          ${heartRate != null ? `<span class="card-status ${heartRate > 100 ? 'status-warning' : 'status-normal'}">${heartRate > 100 ? 'Elevated' : 'Normal'}</span>` : ''}
        </div>
        <div class="card">
          <div class="card-label">Blood Sugar</div>
          <div class="card-value">${sugarValue != null ? sugarValue : '--'} <span style="font-size:14px;font-weight:400;color:#6b7a82;">mg/dL</span></div>
          ${sugarValue != null ? `<span class="card-status ${sugarValue > 140 ? 'status-warning' : 'status-normal'}">${sugarValue > 140 ? 'Monitor' : 'Normal'}</span>` : ''}
        </div>
        <div class="card">
          <div class="card-label">Temperature</div>
          <div class="card-value">${temperature != null ? temperature : '--'} <span style="font-size:14px;font-weight:400;color:#6b7a82;">°F</span></div>
          ${temperature != null ? `<span class="card-status ${temperature > 100.4 ? 'status-danger' : 'status-normal'}">${temperature > 100.4 ? 'Fever' : 'Normal'}</span>` : ''}
        </div>
        <div class="card">
          <div class="card-label">Weight</div>
          <div class="card-value">${weight != null ? weight : '--'} <span style="font-size:14px;font-weight:400;color:#6b7a82;">kg</span></div>
        </div>
        <div class="card">
          <div class="card-label">Medication Adherence</div>
          <div class="card-value">${medsTaken}/${medsScheduled}</div>
          <span class="card-status ${medsTaken >= medsScheduled ? 'status-normal' : 'status-warning'}">${medsTaken >= medsScheduled ? 'On Track' : 'Pending'}</span>
        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">📈 ${durationLabels[reportDuration]} Trends</div>
      ${trends.length > 0 ? `
      <table class="trend-table">
        <thead>
          <tr>
            <th>${reportDuration === 'day' ? 'Hour' : reportDuration === 'week' || reportDuration === 'month' ? 'Day' : 'Month'}</th>
            <th>BP (Systolic)</th>
            <th>Blood Sugar</th>
            <th>Heart Rate</th>
            <th>Temperature</th>
            <th>Weight</th>
          </tr>
        </thead>
        <tbody>
          ${trends.map(t => `
            <tr>
              <td><strong>${t.label}</strong></td>
              <td>${t.bpAvg != null ? t.bpAvg : '—'}</td>
              <td>${t.sugarAvg != null ? t.sugarAvg : '—'}</td>
              <td>${t.hrAvg != null ? t.hrAvg : '—'}</td>
              <td>${t.tempAvg != null ? t.tempAvg : '—'}</td>
              <td>${t.weightAvg != null ? t.weightAvg : '—'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      ` : `
      <div class="no-data">No data available for the selected period.</div>
      `}
    </div>

    <div class="section">
      <div class="section-title">💡 Health Insights</div>
      <div class="insight">
        <h4>Based on your ${durationLabels[reportDuration].toLowerCase()} trends:</h4>
        <ul>
          ${trends.some(t => t.bpAvg != null && t.bpAvg > 140) ? '<li><span class="highlight">Blood Pressure:</span> Your systolic BP has been elevated. Consider reducing salt intake and consult your doctor.</li>' : ''}
          ${trends.some(t => t.sugarAvg != null && t.sugarAvg > 140) ? '<li><span class="highlight">Blood Sugar:</span> Your blood sugar levels are high. Monitor your carbohydrate intake.</li>' : ''}
          ${trends.some(t => t.hrAvg != null && t.hrAvg > 100) ? '<li><span class="highlight">Heart Rate:</span> Your resting heart rate is elevated. Practice deep breathing and reduce stress.</li>' : ''}
          ${trends.some(t => t.tempAvg != null && t.tempAvg > 100.4) ? '<li><span class="highlight">Temperature:</span> You may have a fever. Rest and stay hydrated.</li>' : ''}
          ${trends.some(t => t.tempAvg != null && t.tempAvg < 95) ? '<li><span class="highlight">Temperature:</span> Your temperature is low. Stay warm and monitor for hypothermia symptoms.</li>' : ''}
          ${trends.every(t => 
            (t.bpAvg == null || t.bpAvg <= 140) && 
            (t.sugarAvg == null || (t.sugarAvg >= 70 && t.sugarAvg <= 140)) && 
            (t.hrAvg == null || (t.hrAvg >= 55 && t.hrAvg <= 100)) && 
            (t.tempAvg == null || (t.tempAvg >= 95 && t.tempAvg <= 99.5))
          ) && trends.length > 0 ? '<li>✅ All your vitals are within normal ranges! Keep up the good work!</li>' : ''}
          ${medsTaken < medsScheduled ? `<li><span class="highlight">Medication:</span> You have ${medsScheduled - medsTaken} medication dose(s) pending today. Remember to take them.</li>` : '<li>✅ You\'re on track with your medication schedule!</li>'}
          ${trends.length === 0 ? '<li>Start logging your vitals to get personalized health insights.</li>' : ''}
        </ul>
      </div>
    </div>

    <div class="footer">
      <p>This report was automatically generated from your health data.</p>
      <p>Please consult your healthcare provider for medical advice.</p>
      <p>Report ID: ${Math.random().toString(36).substring(2, 8).toUpperCase()}</p>
    </div>
  </div>
</body>
</html>
      `;

      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Health_Report_${durationLabels[reportDuration]}_${now.toISOString().slice(0, 10)}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setShowReportOptions(false);

      if (addNotification) {
        addNotification({
          type: 'success',
          title: 'Report Downloaded',
          body: `Your ${durationLabels[reportDuration]} health report has been downloaded successfully.`,
          icon: '📊',
        });
      }
    } catch (error) {
      console.error('Error generating health report:', error);
      if (addNotification) {
        addNotification({
          type: 'error',
          title: 'Download Failed',
          body: 'Unable to generate health report. Please try again.',
          icon: '❌',
        });
      }
    }
  };

  useEffect(() => {
    const strip = trustStripRef.current;
    if (!strip) return undefined;

    const interval = setInterval(() => {
      if (isTrustStripPausedRef.current) return;
      const resetPoint = strip.scrollWidth / 2;
      if (resetPoint <= 0) return;
      if (strip.scrollLeft >= resetPoint) {
        strip.scrollLeft -= resetPoint;
      }
      strip.scrollBy({ left: 1, behavior: "auto" });
    }, 24);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadVitalsSection = async () => {
      setVitalsLoading(true);
      setVitalsError("");

      try {
        const [dashboardResult, vitalsResult] = await Promise.allSettled([
          api.get("/api/metrics/dashboard"),
          api.get("/api/vitals", { params: { limit: 100, skip: 0 } }),
        ]);

        if (cancelled) return;

        if (vitalsResult.status === "rejected") {
          throw vitalsResult.reason;
        }

        const dashboardRes = dashboardResult.status === "fulfilled" ? dashboardResult.value : null;
        const vitalsRes = vitalsResult.value;

        setMetricsDashboard(dashboardRes?.data?.metrics || null);
        setRecentVitals(Array.isArray(vitalsRes?.data?.vitals) ? vitalsRes.data.vitals : []);
      } catch (error: any) {
        if (cancelled) return;
        console.error("Error loading vitals:", error);
        setVitalsError(error?.response?.data?.message || "Unable to load vitals");
      } finally {
        if (!cancelled) setVitalsLoading(false);
      }
    };

    loadVitalsSection();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadReminders = async () => {
      setRemindersLoading(true);
      setRemindersError("");

      try {
        const remindersRes = await api.get("/api/reminders/today");
        if (cancelled) return;
        setTodayMedicationReminders(
          Array.isArray(remindersRes?.data?.reminders) ? remindersRes.data.reminders : []
        );
        setTodayAdditionalReminders(
          Array.isArray(remindersRes?.data?.additionalReminders)
            ? remindersRes.data.additionalReminders
            : []
        );
      } catch (error: any) {
        if (cancelled) return;
        setRemindersError(error?.response?.data?.message || "Unable to load today's reminders");
      } finally {
        if (!cancelled) setRemindersLoading(false);
      }
    };

    loadReminders();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadHealthPlan = async () => {
      setHealthPlanLoading(true);
      setHealthPlanError("");

      try {
        const res = await api.get("/api/health-plan/today");
        if (cancelled) return;
        setHealthPlan(res?.data?.plan || null);
      } catch (error: any) {
        if (cancelled) return;
        setHealthPlanError(error?.response?.data?.message || "Unable to load today's care plan");
      } finally {
        if (!cancelled) setHealthPlanLoading(false);
      }
    };

    loadHealthPlan();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchMedications();
  }, []);

  const handleToggleHealthPlanTask = async (taskKey: string) => {
    if (healthPlanActionKey) return;

    try {
      setHealthPlanActionKey(taskKey);
      const res = await api.post("/api/health-plan/toggle", {
        taskKey,
        completed: true,
      });
      setHealthPlan(res?.data?.plan || null);
    } catch (error) {
      console.error("Failed to update care plan task", error);
    } finally {
      setHealthPlanActionKey("");
    }
  };

  const refreshRemindersAndDashboard = async () => {
    const [remindersRes, dashboardRes] = await Promise.all([
      api.get("/api/reminders/today"),
      api.get("/api/metrics/dashboard"),
    ]);

    setTodayMedicationReminders(
      Array.isArray(remindersRes?.data?.reminders) ? remindersRes.data.reminders : []
    );
    setTodayAdditionalReminders(
      Array.isArray(remindersRes?.data?.additionalReminders) ? remindersRes.data.additionalReminders : []
    );
    setMetricsDashboard(dashboardRes?.data?.metrics || null);
  };

  useEffect(() => {
    return onHealthDataUpdated((detail = {}) => {
      if (!String(detail.source || '').startsWith('medication:')) return;
      fetchMedications();
      refreshRemindersAndDashboard();
    }, { debounceMs: 250 });
  }, []);

  const vitalsCards = useMemo(() => {
    const latest = metricsDashboard?.vitals?.latest || {};
    
    const bpRaw = extractBloodPressure(latest) || extractBloodPressure(recentVitals[0]);
    const bp = parseBp(bpRaw);

    let heartRate = null;
    if (Array.isArray(latest.heartRate) && latest.heartRate.length > 0) {
      const hr = latest.heartRate[latest.heartRate.length - 1];
      heartRate = unwrapMetricValue(hr);
    } else if (recentVitals.length > 0) {
      for (let i = 0; i < recentVitals.length; i++) {
        const entry = recentVitals[i];
        if (Array.isArray(entry.heartRate) && entry.heartRate.length > 0) {
          const hr = entry.heartRate[entry.heartRate.length - 1];
          heartRate = unwrapMetricValue(hr);
          break;
        }
      }
    }

    let temperature = null;
    if (Array.isArray(latest.temperature) && latest.temperature.length > 0) {
      const temp = latest.temperature[latest.temperature.length - 1];
      temperature = unwrapMetricValue(temp);
    } else if (recentVitals.length > 0) {
      for (let i = 0; i < recentVitals.length; i++) {
        const entry = recentVitals[i];
        if (Array.isArray(entry.temperature) && entry.temperature.length > 0) {
          const temp = entry.temperature[entry.temperature.length - 1];
          temperature = unwrapMetricValue(temp);
          break;
        }
      }
    }

    let weight = null;
    let weightTime = null;
    if (Array.isArray(latest.weight) && latest.weight.length > 0) {
      const w = latest.weight[latest.weight.length - 1];
      weight = unwrapMetricValue(w);
      weightTime = latest.time;
    } else if (recentVitals.length > 0) {
      for (let i = 0; i < recentVitals.length; i++) {
        const entry = recentVitals[i];
        if (Array.isArray(entry.weight) && entry.weight.length > 0) {
          const w = entry.weight[entry.weight.length - 1];
          weight = unwrapMetricValue(w);
          weightTime = entry.createdAt;
          break;
        }
      }
    }

    let sugarValue = null;
    let sugarTime = null;
    if (Array.isArray(latest.bloodSugar) && latest.bloodSugar.length > 0) {
      const sugar = latest.bloodSugar[latest.bloodSugar.length - 1];
      sugarValue = unwrapMetricValue(sugar);
      sugarTime = latest.time;
    } else if (recentVitals.length > 0) {
      for (let i = 0; i < recentVitals.length; i++) {
        const entry = recentVitals[i];
        if (Array.isArray(entry.bloodSugar) && entry.bloodSugar.length > 0) {
          const sugar = entry.bloodSugar[entry.bloodSugar.length - 1];
          sugarValue = unwrapMetricValue(sugar);
          sugarTime = entry.createdAt;
          break;
        }
      }
    }

    const bpEntry = recentVitals.find(e => e?.bloodPressure && e.bloodPressure.length > 0);
    const sugarEntry = recentVitals.find(e => e?.bloodSugar && e.bloodSugar.length > 0);
    const hrEntry = recentVitals.find(e => e?.heartRate && e.heartRate.length > 0);
    const tempEntry = recentVitals.find(e => e?.temperature && e.temperature.length > 0);

    const medsSummary = metricsDashboard?.medications || {};
    const medsTaken = typeof medsSummary?.todayTaken === "number" ? medsSummary.todayTaken : 0;
    const medsScheduled = typeof medsSummary?.todayScheduled === "number" ? medsSummary.todayScheduled : 0;
    const medsPending = Math.max(medsScheduled - medsTaken, 0);

    const bpNormal = bp ? bp.systolic < 130 && bp.diastolic < 85 : false;
    const heartRateNum = typeof heartRate === "number" ? heartRate : null;
    const temperatureNum = typeof temperature === "number" ? temperature : null;
    const weightNum = typeof weight === "number" ? weight : null;

    return [
      {
        key: "bp",
        metricSlug: "blood-pressure",
        icon: <HeartPulse size={16} />,
        iconBg: C.pink,
        iconFg: C.pinkFg,
        label: "Blood Pressure",
        value: bpRaw || "--",
        unit: bpRaw ? "mmHg" : undefined,
        status: bpRaw ? (bpNormal ? "Normal" : "Borderline") : "No data",
        statusColor: bpRaw ? (bpNormal ? C.good : C.warn) : C.mutedFg,
        statusBg: bpRaw ? (bpNormal ? C.goodSoft : C.warnSoft) : C.border,
        time: formatTimeLabel(bpEntry?.createdAt || latest?.time),
      },
      {
        key: "sugar",
        metricSlug: "blood-sugar",
        icon: <Droplet size={16} />,
        iconBg: C.blue,
        iconFg: C.blueFg,
        label: "Blood Sugar",
        value: sugarValue != null ? String(sugarValue) : "--",
        unit: sugarValue != null ? "mg/dL" : undefined,
        status: sugarValue != null ? (isWithinRange(sugarValue, 70, 140) ? "Normal" : "Watch") : "No data",
        statusColor: sugarValue != null ? (isWithinRange(sugarValue, 70, 140) ? C.good : C.warn) : C.mutedFg,
        statusBg: sugarValue != null ? (isWithinRange(sugarValue, 70, 140) ? C.goodSoft : C.warnSoft) : C.border,
        time: formatTimeLabel(sugarTime || sugarEntry?.createdAt || latest?.time),
      },
      {
        key: "hr",
        metricSlug: "heart-rate",
        icon: <Heart size={16} />,
        iconBg: C.pink,
        iconFg: C.pinkFg,
        label: "Heart Rate",
        value: heartRateNum != null ? String(heartRateNum) : "--",
        unit: heartRateNum != null ? "bpm" : undefined,
        status: heartRateNum != null ? (isWithinRange(heartRateNum, 55, 100) ? "Normal" : "Watch") : "No data",
        statusColor: heartRateNum != null ? (isWithinRange(heartRateNum, 55, 100) ? C.good : C.warn) : C.mutedFg,
        statusBg: heartRateNum != null ? (isWithinRange(heartRateNum, 55, 100) ? C.goodSoft : C.warnSoft) : C.border,
        time: formatTimeLabel(hrEntry?.createdAt || latest?.time),
      },
      {
        key: "temp",
        metricSlug: "temperature",
        icon: <Thermometer size={16} />,
        iconBg: C.peach,
        iconFg: C.peachFg,
        label: "Temperature",
        value: temperatureNum != null ? String(temperatureNum) : "--",
        unit: temperatureNum != null ? "°F" : undefined,
        status: temperatureNum != null ? (isWithinRange(temperatureNum, 95, 99.5) ? "Normal" : "Watch") : "No data",
        statusColor: temperatureNum != null ? (isWithinRange(temperatureNum, 95, 99.5) ? C.good : C.warn) : C.mutedFg,
        statusBg: temperatureNum != null ? (isWithinRange(temperatureNum, 95, 99.5) ? C.goodSoft : C.warnSoft) : C.border,
        time: formatTimeLabel(tempEntry?.createdAt || latest?.time),
      },
      {
        key: "weight",
        metricSlug: "weight",
        icon: <Scale size={16} />,
        iconBg: C.primarySoft,
        iconFg: C.primary,
        label: "Weight",
        value: weightNum != null ? String(weightNum) : "--",
        unit: weightNum != null ? "kg" : undefined,
        status: "No trend",
        statusColor: C.good,
        statusBg: C.goodSoft,
        time: formatTimeLabel(weightTime || latest?.time),
      },
      {
        key: "medication",
        metricSlug: "medication",
        icon: <Pill size={16} />,
        iconBg: C.primarySoft,
        iconFg: C.primary,
        label: "Medication",
        value: `${medsTaken}/${medsScheduled}`,
        unit: "taken",
        status: medsPending > 0 ? `${medsPending} pending` : "On track",
        statusColor: medsPending > 0 ? C.peachFg : C.good,
        statusBg: medsPending > 0 ? C.peach : C.goodSoft,
        time: formatTimeLabel(new Date()),
      },
    ];
  }, [metricsDashboard, recentVitals]);

  const carePlan = useMemo(() => {
    const tasks = healthPlan?.tasks || {};
    const rows = CARE_PLAN_TASK_ORDER.map((key) => {
      const task = tasks[key] || {};
      return {
        key,
        done: Boolean(task.completed),
        label: task.label || DEFAULT_TASK_LABELS[key],
        time: task.completed ? formatTimeLabel(task.completedAt) : "Due today",
      };
    });
    const completed = typeof healthPlan?.completionCount === "number"
      ? healthPlan.completionCount
      : rows.filter((item) => item.done).length;
    return { rows, completed, total: rows.length };
  }, [healthPlan]);

  useEffect(() => {
    if (healthPlanLoading || healthPlanError) return;

    const pendingRows = carePlan.rows
      .filter((rowItem) => !rowItem.done)
      .sort((left, right) => {
        const priority = (rowKey: string) => {
          if (rowKey === "hydration") return 0;
          if (rowKey === "medication") return 1;
          if (rowKey === "bp") return 2;
          if (rowKey === "symptoms") return 3;
          if (rowKey === "followUpQuestions") return 4;
          return 5;
        };
        return priority(left.key) - priority(right.key);
      });

    const signature = pendingRows.map((item) => `${item.key}:${item.time}:${item.done}`).join("|");
    if (signature === carePlanSignatureRef.current && carePlanNotifiedRef.current.size > 0) return;

    carePlanSignatureRef.current = signature;

    if (carePlanQueueTimerRef.current) {
      window.clearTimeout(carePlanQueueTimerRef.current);
      carePlanQueueTimerRef.current = null;
    }

    if (!pendingRows.length) return;

    const announceNext = (index: number) => {
      const item = pendingRows[index];
      if (!item) return;

      if (!carePlanNotifiedRef.current.has(item.key)) {
        const isWater = item.key === "hydration";
        addNotification({
          type: "care_plan_step",
          title: isWater ? "Time to drink water" : "Your next health step is ready",
          body: isWater
            ? "Drink water now to stay on top of your hydration goal."
            : `${item.label} is next in your care plan${item.time ? ` (${item.time})` : ""}.`,
          icon: isWater ? "💧" : "🔔",
          data: {
            carePlanKey: item.key,
            label: item.label,
            time: item.time,
          },
        });
        carePlanNotifiedRef.current.add(item.key);
      }

      const nextDelay = item.key === "hydration" ? 30000 : 45000;
      carePlanQueueTimerRef.current = window.setTimeout(() => {
        announceNext(index + 1);
      }, nextDelay);
    };

    announceNext(0);

    return () => {
      if (carePlanQueueTimerRef.current) {
        window.clearTimeout(carePlanQueueTimerRef.current);
        carePlanQueueTimerRef.current = null;
      }
    };
  }, [carePlan.rows, healthPlanLoading, healthPlanError, addNotification]);

  const healthStatus = useMemo(() => {
    if (vitalsLoading || remindersLoading) {
      return {
        label: "Loading",
        message: "Analyzing your latest health data...",
        color: C.mutedFg,
        soft: C.border,
        readingsLogged: 0,
        remindersPending: 0,
      };
    }

    if (vitalsError) {
      return {
        label: "Unavailable",
        message: "Unable to load latest vitals right now.",
        color: C.warn,
        soft: C.warnSoft,
        readingsLogged: 0,
        remindersPending: 0,
      };
    }

    const latest = metricsDashboard?.vitals?.latest || {};
    const todayReadingsCount =
      typeof metricsDashboard?.vitals?.today?.count === "number"
        ? metricsDashboard.vitals.today.count
        : recentVitals.filter((entry) => {
            const d = entry?.createdAt ? new Date(entry.createdAt) : null;
            return d && !Number.isNaN(d.getTime()) && d.toDateString() === new Date().toDateString();
          }).length;

    const pendingMedicationDoses = todayMedicationReminders.reduce((sum, med) => {
      const unresolved = (Array.isArray(med?.scheduledTimes) ? med.scheduledTimes : []).filter(
        (dose: any) => !dose?.taken && !dose?.skippedToday
      ).length;
      return sum + unresolved;
    }, 0);

    const pendingAdditional = todayAdditionalReminders.filter((item) => {
      const t = String(item?.type || "").toLowerCase();
      return ["vitals", "blood_sugar", "appointment", "lab"].includes(t);
    }).length;

    const remindersPending = pendingMedicationDoses + pendingAdditional;

    const bpRaw = extractBloodPressure(latest) || extractBloodPressure(recentVitals[0]);
    const bp = parseBp(bpRaw);
    const sugarValue = getLatestMetricValue(latest, "bloodSugar") ?? getMetricValueFromEntries(recentVitals, "bloodSugar");
    const heartRate = getLatestMetricValue(latest, "heartRate") ?? getMetricValueFromEntries(recentVitals, "heartRate");
    const temperature = getLatestMetricValue(latest, "temperature") ?? getMetricValueFromEntries(recentVitals, "temperature");
    const oxygenLevel = getLatestMetricValue(latest, "oxygenLevel") ?? getMetricValueFromEntries(recentVitals, "oxygenLevel");

    const abnormalChecks = [
      bp ? !(bp.systolic < 130 && bp.diastolic < 85) : false,
      sugarValue != null ? !isWithinRange(sugarValue, 70, 140) : false,
      heartRate != null ? !isWithinRange(heartRate, 55, 100) : false,
      temperature != null ? !isWithinRange(temperature, 95, 99.5) : false,
      oxygenLevel != null ? !isWithinRange(oxygenLevel, 95, 100) : false,
    ];
    const abnormalCount = abnormalChecks.filter(Boolean).length;

    const critical =
      (bp && (bp.systolic >= 160 || bp.diastolic >= 100)) ||
      (sugarValue != null && sugarValue >= 220) ||
      (heartRate != null && (heartRate < 45 || heartRate > 120)) ||
      (temperature != null && temperature >= 100.4) ||
      (oxygenLevel != null && oxygenLevel < 92);

    if (critical || abnormalCount >= 2) {
      return {
        label: "Needs Attention",
        message: "Some readings are outside safe ranges.",
        color: C.peachFg,
        soft: C.peach,
        readingsLogged: todayReadingsCount,
        remindersPending,
      };
    }

    if (abnormalCount === 1 || remindersPending > 2) {
      return {
        label: "Monitor",
        message: "Keep tracking closely and complete pending tasks.",
        color: C.warn,
        soft: C.warnSoft,
        readingsLogged: todayReadingsCount,
        remindersPending,
      };
    }

    const hasAnyReading = Boolean(latest?.time) || recentVitals.length > 0;
    if (!hasAnyReading) {
      return {
        label: "Awaiting Data",
        message: "Add your first reading today to assess your status.",
        color: C.mutedFg,
        soft: C.border,
        readingsLogged: 0,
        remindersPending,
      };
    }

    return {
      label: "Stable",
      message: "Current vitals are within expected ranges.",
      color: C.good,
      soft: C.goodSoft,
      readingsLogged: todayReadingsCount,
      remindersPending,
    };
  }, [
    metricsDashboard,
    recentVitals,
    todayMedicationReminders,
    todayAdditionalReminders,
    vitalsLoading,
    remindersLoading,
    vitalsError,
  ]);

  const emergencyStatusSummary = useMemo(() => {
    if (vitalsLoading || remindersLoading) {
      return {
        level: "Loading",
        title: "Emergency level: Loading",
        description: "We are checking your latest health readings.",
        detail: "Your emergency status will update as soon as the data finishes loading.",
        color: C.mutedFg,
        bg: C.border,
      };
    }

    if (emergencyAlert) {
      return {
        level: emergencyAlert.level === "critical" ? "Critical" : "Warning",
        title: emergencyAlert.level === "critical" ? "Emergency level: Critical" : "Emergency level: Warning",
        description: emergencyAlert.message,
        detail: emergencyAlert.conditions.length
          ? `Current issue: ${emergencyAlert.conditions.join(", ")}`
          : "Monitor the patient closely and review current readings.",
        color: emergencyAlert.level === "critical" ? C.danger : C.warn,
        bg: emergencyAlert.level === "critical" ? C.dangerSoft : C.warnSoft,
      };
    }

    if (healthStatus.label === "Stable") {
      return {
        level: "Normal",
        title: "Emergency level: Normal",
        description: "Current vital signs are within expected ranges.",
        detail: "No immediate medical escalation is required based on the latest readings.",
        color: C.good,
        bg: C.goodSoft,
      };
    }

    if (healthStatus.label === "Monitor") {
      return {
        level: "Watch",
        title: "Emergency level: Watch",
        description: "Some readings are outside the usual range.",
        detail: "Continue monitoring and follow the care plan to avoid deterioration.",
        color: C.warn,
        bg: C.warnSoft,
      };
    }

    return {
      level: "Needs attention",
      title: "Emergency level: Needs attention",
      description: "There are abnormal readings that need review.",
      detail: "Please check the latest vitals and consult a clinician if symptoms worsen.",
      color: C.peachFg,
      bg: C.peach,
    };
  }, [emergencyAlert, healthStatus, remindersLoading, vitalsLoading]);

  const emergencyStatusVitals = useMemo(() => {
    const latest = metricsDashboard?.vitals?.latest || {};
    return {
      bp: emergencyAlert?.vitals?.bp || extractBloodPressure(latest) || extractBloodPressure(recentVitals[0]),
      heartRate: emergencyAlert?.vitals?.heartRate ?? getLatestMetricValue(latest, "heartRate") ?? getMetricValueFromEntries(recentVitals, "heartRate"),
      sugar: emergencyAlert?.vitals?.sugar ?? getLatestMetricValue(latest, "bloodSugar") ?? getMetricValueFromEntries(recentVitals, "bloodSugar"),
      temperature: emergencyAlert?.vitals?.temperature ?? getLatestMetricValue(latest, "temperature") ?? getMetricValueFromEntries(recentVitals, "temperature"),
    };
  }, [emergencyAlert, metricsDashboard, recentVitals]);

  const emergencyDetection = useMemo(() => {
    if (vitalsLoading || !metricsDashboard) return null;

    const latest = metricsDashboard?.vitals?.latest || {};
    
    const bpRaw = extractBloodPressure(latest) || extractBloodPressure(recentVitals[0]);
    const bp = parseBp(bpRaw);

    const sugarValue = getLatestMetricValue(latest, 'bloodSugar') ?? getMetricValueFromEntries(recentVitals, 'bloodSugar');
    const heartRate = getLatestMetricValue(latest, 'heartRate') ?? getMetricValueFromEntries(recentVitals, 'heartRate');
    const temperature = getLatestMetricValue(latest, 'temperature') ?? getMetricValueFromEntries(recentVitals, 'temperature');
    const oxygenLevel = getLatestMetricValue(latest, 'oxygenLevel') ?? getMetricValueFromEntries(recentVitals, 'oxygenLevel');

    const conditions: string[] = [];
    let level: "critical" | "warning" = "warning";
    const messages: string[] = [];

    // Hypertension
    if (bp && bp.systolic >= 180) {
      conditions.push("Hypertensive Crisis");
      level = "critical";
      messages.push(`Blood pressure critically high: ${bp.systolic}/${bp.diastolic} mmHg`);
    } else if (bp && bp.systolic >= 160) {
      conditions.push("Severe Hypertension");
      if (level !== "critical") level = "critical";
      messages.push(`Severe hypertension: ${bp.systolic}/${bp.diastolic} mmHg`);
    } else if (bp && (bp.systolic >= 140 || bp.diastolic >= 90)) {
      conditions.push("Hypertension");
      messages.push(`High blood pressure: ${bp.systolic}/${bp.diastolic} mmHg`);
    }

    // Hypotension
    if (bp && bp.systolic < 90) {
      conditions.push("Hypotension");
      if (level !== "critical") level = "critical";
      messages.push(`Low blood pressure: ${bp.systolic}/${bp.diastolic} mmHg`);
    }

    // Hyperglycemia
    if (sugarValue != null && sugarValue >= 300) {
      conditions.push("Severe Hyperglycemia");
      level = "critical";
      messages.push(`Blood sugar critically high: ${sugarValue} mg/dL`);
    } else if (sugarValue != null && sugarValue >= 250) {
      conditions.push("Hyperglycemia");
      messages.push(`High blood sugar: ${sugarValue} mg/dL`);
    }

    // Hypoglycemia
    if (sugarValue != null && sugarValue < 50) {
      conditions.push("Severe Hypoglycemia");
      level = "critical";
      messages.push(`Blood sugar dangerously low: ${sugarValue} mg/dL`);
    } else if (sugarValue != null && sugarValue < 70) {
      conditions.push("Hypoglycemia");
      messages.push(`Low blood sugar: ${sugarValue} mg/dL`);
    }

    // Tachycardia
    if (heartRate != null && heartRate > 150) {
      conditions.push("Severe Tachycardia");
      level = "critical";
      messages.push(`Heart rate critically high: ${heartRate} bpm`);
    } else if (heartRate != null && heartRate > 120) {
      conditions.push("Tachycardia");
      messages.push(`Elevated heart rate: ${heartRate} bpm`);
    }

    // Bradycardia
    if (heartRate != null && heartRate < 40) {
      conditions.push("Severe Bradycardia");
      level = "critical";
      messages.push(`Heart rate critically low: ${heartRate} bpm`);
    } else if (heartRate != null && heartRate < 50) {
      conditions.push("Bradycardia");
      messages.push(`Low heart rate: ${heartRate} bpm`);
    }

    // Cardiac Arrest indicators
    if (heartRate != null && heartRate === 0) {
      conditions.push("Possible Cardiac Arrest");
      level = "critical";
      messages.push("Heart rate is zero - possible cardiac arrest");
    } else if (heartRate != null && heartRate < 30 && oxygenLevel != null && oxygenLevel < 85) {
      conditions.push("Possible Cardiac Arrest");
      level = "critical";
      messages.push("Critically low heart rate and oxygen - possible cardiac arrest");
    }

    // Fever / Hyperthermia
    if (temperature != null && temperature >= 105) {
      conditions.push("Hyperthermia");
      level = "critical";
      messages.push(`Dangerously high temperature: ${temperature}°F`);
    } else if (temperature != null && temperature >= 103) {
      conditions.push("High Fever");
      messages.push(`High fever: ${temperature}°F`);
    } else if (temperature != null && temperature >= 100.4) {
      conditions.push("Fever");
      messages.push(`Fever detected: ${temperature}°F`);
    }

    // Hypothermia
    if (temperature != null && temperature < 90) {
      conditions.push("Severe Hypothermia");
      level = "critical";
      messages.push(`Dangerously low temperature: ${temperature}°F`);
    } else if (temperature != null && temperature < 95) {
      conditions.push("Hypothermia");
      messages.push(`Low body temperature: ${temperature}°F`);
    }

    // Hypoxia
    if (oxygenLevel != null && oxygenLevel < 85) {
      conditions.push("Severe Hypoxia");
      level = "critical";
      messages.push(`Oxygen level critically low: ${oxygenLevel}%`);
    } else if (oxygenLevel != null && oxygenLevel < 92) {
      conditions.push("Hypoxia");
      messages.push(`Low oxygen level: ${oxygenLevel}%`);
    }

    // Blood Loss / Shock indicators
    if (bp && heartRate != null) {
      const previousBpEntry = [...recentVitals].reverse().find((entry) => !!extractBloodPressure(entry));
      const previousHrEntry = [...recentVitals].reverse().find((entry) => getLatestMetricValue(entry, 'heartRate') != null);

      let previousSystolic = null;
      let previousHr = null;

      if (previousBpEntry) {
        const prevBpRaw = extractBloodPressure(previousBpEntry);
        const prevBp = parseBp(prevBpRaw);
        if (prevBp) previousSystolic = prevBp.systolic;
      }

      if (previousHrEntry) {
        previousHr = getLatestMetricValue(previousHrEntry, 'heartRate');
      }

      const bpDrop = previousSystolic != null ? previousSystolic - bp.systolic : 0;
      const hrRise = previousHr != null ? heartRate - previousHr : 0;

      if (bpDrop >= 20 && hrRise >= 15) {
        conditions.push("Possible Blood Loss / Shock");
        level = "critical";
        messages.push(`Sudden BP drop (${bpDrop} mmHg) and HR rise (${hrRise} bpm) - possible blood loss or shock`);
      }
    }

    if (conditions.length === 0) return null;

    return {
      active: true,
      level,
      conditions,
      message: messages.join(". ") + ".",
      vitals: { bp: bpRaw, sugar: sugarValue, heartRate, temperature, oxygenLevel },
      timestamp: new Date(),
    };
  }, [metricsDashboard, recentVitals, vitalsLoading]);

  const dismissEmergencyAlert = () => {
    if (emergencyDetectionKey) {
      lastEmergencyKeyRef.current = emergencyDetectionKey;
    }
    setShowEmergencyModal(false);
    setEmergencyAlert(null);
  };

  const emergencyDetectionKey = emergencyDetection
    ? JSON.stringify({
        level: emergencyDetection.level,
        conditions: emergencyDetection.conditions,
        message: emergencyDetection.message,
        vitals: emergencyDetection.vitals,
      })
    : "";

  useEffect(() => {
    if (!emergencyDetection) return;

    if (lastEmergencyKeyRef.current === emergencyDetectionKey && !emergencyAlert) {
      return;
    }

    if (emergencyAlert) {
      return;
    }

    setEmergencyAlert(emergencyDetection);
    setShowEmergencyModal(true);
    lastEmergencyKeyRef.current = emergencyDetectionKey;

    if (emergencyDetection.level === "critical") {
      addNotification({
        type: "emergency",
        title: "EMERGENCY ALERT",
        body: emergencyDetection.message,
        icon: "🚨",
      });
    }
  }, [emergencyDetection, emergencyDetectionKey, emergencyAlert, addNotification]);

  useEffect(() => {
    if (!emergencyAlert) return;

    const emitEmergency = async () => {
      const { socket: socketInstance } = await import("../lib/socket");
      if (socketInstance && socketInstance.connected) {
        socketInstance.emit("emergency:alert", {
          userId: localStorage.getItem("userId"),
          conditions: emergencyAlert.conditions,
          message: emergencyAlert.message,
          level: emergencyAlert.level,
          vitals: emergencyAlert.vitals,
          timestamp: emergencyAlert.timestamp,
        });
      }
    };

    emitEmergency();
  }, [emergencyAlert]);

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        background: "var(--bg)",
        ...row,
        justifyContent: "stretch",
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        style={{
          position: "relative",
          ...col,
          width: "100%",
          maxWidth: "none",
          minHeight: "100vh",
          background: "var(--bg)",
          boxShadow: "none",
          paddingBottom: 90,
        }}
      >
        <FeatureHeader title="Remote Monitoring" subtitle="Track your health, get alert and stay connected." onBack={() => navigate(-1)} />

   
        {/* Pulse animation keyframes */}
        <style>{`
          @keyframes pulse-dot {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.4; transform: scale(0.8); }
          }
          @keyframes monitoring-shimmer {
            0% { opacity: 0.55; }
            50% { opacity: 1; }
            100% { opacity: 0.55; }
          }
        `}</style>

        <div
          style={{
            margin: "12px 18px 0",
            background: C.primarySoft,
            borderRadius: 18,
            padding: 16,
            position: "relative",
            overflow: "hidden",
            ...row,
            gap: 12,
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 9999,
              background: "#fff",
              border: `2px solid ${healthStatus.soft}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Activity size={32} color={healthStatus.color} />
          </div>
          <div style={{ ...col, flex: 1, gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.fg }}>Today's Health Status</span>
            <div style={{ ...row, gap: 6 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: healthStatus.color }}>{healthStatus.label}</span>
              <span style={{ color: healthStatus.color, fontSize: 20, lineHeight: 1 }}>•</span>
            </div>
            <span style={{ fontSize: 11, color: C.mutedFg }}>{healthStatus.message}</span>
            <div style={{ ...row, gap: 10, marginTop: 6, flexWrap: "wrap" }}>
              <span style={{ ...row, gap: 4, fontSize: 10, color: C.fg, background: "#fff", padding: "3px 8px", borderRadius: 8 }}>
                <ClipboardList size={11} color={C.primary} /> {healthStatus.readingsLogged} readings logged
              </span>
              <span style={{ ...row, gap: 4, fontSize: 10, color: C.fg, background: "#fff", padding: "3px 8px", borderRadius: 8 }}>
                <Bell size={11} color={C.primary} /> {healthStatus.remindersPending} reminders pending
              </span>
            </div>
          </div>
        </div>

        <div style={{ ...row, justifyContent: "space-between", padding: "18px 18px 8px" }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.fg }}>Your Vitals</span>
          <button
            type="button"
            onClick={() => navigate("/health-metrics")}
            style={{ fontSize: 12, fontWeight: 600, color: C.primary, background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
          >
            View All ›
          </button>
        </div>
        {vitalsError ? (
          <div style={{ margin: "0 18px", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, color: C.peachFg, fontSize: 12 }}>
            {vitalsError}
          </div>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "0 18px" }}>
          {vitalsLoading ? (
            ["Blood Pressure", "Blood Sugar", "Heart Rate", "Temperature", "Weight", "Medication"].map((label) => (
              <div
                key={label}
                style={{
                  height: 112,
                  border: `1px solid ${C.border}`,
                  borderRadius: 16,
                  background: C.primarySofter,
                  padding: 12,
                  boxSizing: "border-box",
                  animation: "monitoring-shimmer 1.4s ease-in-out infinite",
                }}
              >
                <div style={{ width: 30, height: 30, borderRadius: 9999, background: C.primarySoft }} />
                <div style={{ width: "68%", height: 10, borderRadius: 4, background: C.primarySoft, marginTop: 12 }} />
                <div style={{ width: "42%", height: 18, borderRadius: 4, background: C.border, marginTop: 10 }} />
                <span style={{ display: "block", fontSize: 10, color: C.mutedFg, marginTop: 8 }}>{label}</span>
              </div>
            ))
          ) : (
            vitalsCards.map((card) => (
              <VitalCard
                key={card.key}
                icon={card.icon}
                iconBg={card.iconBg}
                iconFg={card.iconFg}
                label={card.label}
                value={card.value}
                unit={card.unit}
                status={card.status}
                statusColor={card.statusColor}
                statusBg={card.statusBg}
                time={card.time}
                onClick={() => {
                  if (card.metricSlug === "medication") {
                    setShowAddMedicationModal(false);
                    return;
                  }
                  navigate(`/metrics/${card.metricSlug}`);
                }}
              />
            ))
          )}
        </div>

        <div style={{ ...row, justifyContent: "center", gap: 6, marginTop: 14 }}>
          <span style={{ width: 20, height: 5, borderRadius: 999, background: C.primary }} />
          <span style={{ width: 5, height: 5, borderRadius: 999, background: C.border }} />
          <span style={{ width: 5, height: 5, borderRadius: 999, background: C.border }} />
        </div>

        {/* Medications Section */}
        <div style={{ margin: "16px 18px 0" }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Pill size={20} color={C.primary} />
              <span style={{ fontSize: 16, fontWeight: 700, color: C.fg }}>My Medications</span>
              <span style={{ fontSize: 12, color: C.mutedFg, background: C.border, padding: '2px 8px', borderRadius: 999 }}>
                {medications.filter(m => m.isActive !== false).length} active
              </span>
            </div>
            <button
              onClick={openAddModal}
              style={{
                padding: '8px 16px',
                borderRadius: 999,
                border: 'none',
                background: C.primary,
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Plus size={14} /> Add Medication
            </button>
          </div>

          {medicationsLoading ? (
            <div style={{ textAlign: 'center', padding: 30, color: C.mutedFg }}>
              Loading medications...
            </div>
          ) : medicationsError ? (
            <div style={{ 
              background: C.dangerSoft, 
              padding: 12, 
              borderRadius: 12, 
              color: C.danger,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <AlertCircle size={16} />
              {medicationsError}
            </div>
          ) : medications.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 30,
              background: C.border,
              borderRadius: 16,
              color: C.mutedFg
            }}>
              <Pill size={32} style={{ display: 'block', margin: '0 auto 8px', color: C.mutedFg }} />
              <p style={{ margin: 0, fontSize: 14 }}>No medications added yet</p>
              <p style={{ margin: '4px 0 0', fontSize: 12 }}>Click "Add Medication" to start tracking</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {medications.map((med) => {
                const isActive = med.isActive !== false;
                const isCompleted = med.isCompleted === true;
                
                const dosages = med.dosages || [];
                const times = dosages.map((d: any) => d.time);
                const takenCount = dosages.filter((d: any) => d.taken).length || 0;
                const totalDoses = dosages.length || 0;
                const adherence = med.adherencePercentage || 0;
                
                return (
                  <div
                    key={med._id}
                    style={{
                      background: C.bg,
                      border: `1px solid ${isActive ? C.goodSoft : isCompleted ? C.primarySoft : C.warnSoft}`,
                      borderRadius: 12,
                      padding: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s',
                      opacity: isCompleted ? 0.7 : 1,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: isActive ? C.goodSoft : isCompleted ? C.primarySoft : C.warnSoft,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Pill size={20} color={isActive ? C.good : isCompleted ? C.primary : C.warn} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: C.fg }}>
                            {med.medicineName || med.name}
                          </span>
                          <span style={{ fontSize: 12, color: C.mutedFg }}>
                            {med.strength || med.dosage}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: isActive ? C.goodSoft : isCompleted ? C.primarySoft : C.warnSoft,
                              color: isActive ? C.good : isCompleted ? C.primary : C.warn,
                            }}
                          >
                            {isActive ? 'Active' : isCompleted ? 'Completed' : 'Inactive'}
                          </span>
                          {totalDoses > 0 && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: 999,
                                background: takenCount === totalDoses ? C.goodSoft : C.warnSoft,
                                color: takenCount === totalDoses ? C.good : C.warn,
                              }}
                            >
                              {takenCount}/{totalDoses} taken
                            </span>
                          )}
                          {adherence > 0 && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: 999,
                                background: adherence >= 80 ? C.goodSoft : C.warnSoft,
                                color: adherence >= 80 ? C.good : C.warn,
                              }}
                            >
                              {adherence}% adherence
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, color: C.mutedFg }}>
                            <Clock size={12} style={{ display: 'inline', marginRight: 4 }} />
                            {med.frequency || 'Not specified'}
                          </span>
                          {times.length > 0 && (
                            <span style={{ fontSize: 12, color: C.mutedFg }}>
                              <Calendar size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {times.join(', ')}
                            </span>
                          )}
                          {med.doctorName && (
                            <span style={{ fontSize: 12, color: C.mutedFg }}>
                              👨‍⚕️ {med.doctorName}
                            </span>
                          )}
                          {med.category && (
                            <span style={{ fontSize: 12, color: C.mutedFg }}>
                              📋 {med.category}
                            </span>
                          )}
                          {med.duration && (
                            <span style={{ fontSize: 12, color: C.mutedFg }}>
                              📅 {med.duration}
                            </span>
                          )}
                          {med.durationEndDate && (
                            <span style={{ fontSize: 12, color: C.mutedFg }}>
                              Ends: {new Date(med.durationEndDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {med.instructions && (
                          <div style={{ marginTop: 4 }}>
                            <span style={{ fontSize: 11, color: C.mutedFg }}>
                              📝 {med.instructions}
                            </span>
                          </div>
                        )}
                        {med.sideEffects && med.sideEffects.length > 0 && (
                          <div style={{ marginTop: 2 }}>
                            <span style={{ fontSize: 11, color: C.mutedFg }}>
                              ⚠️ Side Effects: {Array.isArray(med.sideEffects) ? med.sideEffects.join(', ') : med.sideEffects}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={() => openEditModal(med)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: `1px solid ${C.border}`,
                          background: C.bg,
                          color: C.primary,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteMedication(med._id)}
                        disabled={deletingMedicationId === med._id}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: `1px solid ${C.dangerSoft}`,
                          background: C.dangerSoft,
                          color: C.danger,
                          cursor: deletingMedicationId === med._id ? 'not-allowed' : 'pointer',
                          opacity: deletingMedicationId === med._id ? 0.5 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Trash2 size={14} />
                        {deletingMedicationId === med._id ? '...' : ''}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {emergencyAlert && (
          <div
            style={{
              margin: "12px 18px 0",
              background: emergencyAlert.level === "critical" ? C.dangerSoft : C.warnSoft,
              border: `2px solid ${emergencyAlert.level === "critical" ? C.danger : C.warn}`,
              borderRadius: 18,
              padding: 16,
              ...row,
              gap: 12,
              animation: "pulse 2s infinite",
              cursor: "pointer",
            }}
            onClick={() => setShowEmergencyModal(true)}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 9999,
                background: "#fff",
                border: `2px solid ${emergencyAlert.level === "critical" ? C.danger : C.warn}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Siren size={32} color={emergencyAlert.level === "critical" ? C.danger : C.warn} />
            </div>
            <div style={{ ...col, flex: 1, gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.fg }}>
                {emergencyAlert.level === "critical" ? "EMERGENCY ALERT" : "Health Warning"}
              </span>
              <div style={{ ...row, gap: 6 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: emergencyAlert.level === "critical" ? C.danger : C.warn }}>
                  {emergencyAlert.conditions.join(", ")}
                </span>
              </div>
              <span style={{ fontSize: 11, color: C.mutedFg }}>{emergencyAlert.message}</span>
              <span style={{ fontSize: 10, color: C.mutedFg }}>
                <Clock size={10} style={{ display: 'inline', marginRight: 4 }} />
                Detected: {emergencyAlert.timestamp.toLocaleTimeString()}
              </span>
            </div>
          </div>
        )}

             {/* Emergency Level Status - Redesigned */}
<div style={{
  margin: "16px",
  padding: "18px",
  borderRadius: "12px",
  border: `1px solid ${C.border}`,
  background: "#fff",
}}>
  {/* Status header */}
  <div style={{
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
  }}>
    <div style={{
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: emergencyStatusSummary.color,
      animation: emergencyStatusSummary.level === "Critical" ? "pulse-dot 1.5s ease-in-out infinite" : "none",
    }} />
    <span style={{
      fontSize: 12,
      fontWeight: 600,
      color: C.mutedFg,
      textTransform: "uppercase",
    }}>
      Emergency Status
    </span>
    <span style={{
      fontSize: 11,
      fontWeight: 600,
      color: emergencyStatusSummary.color,
      background: emergencyStatusSummary.bg,
      padding: "2px 10px",
      borderRadius: 6,
      border: `1px solid ${emergencyStatusSummary.color}30`,
    }}>
      {emergencyStatusSummary.level}
    </span>
  </div>

  {/* Main content */}
  <div style={{
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
  }}>
    <div style={{
      width: 50,
      height: 50,
      borderRadius: 10,
      background: emergencyStatusSummary.bg,
      border: `1px solid ${emergencyStatusSummary.color}30`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <Siren size={26} color={emergencyStatusSummary.color} />
    </div>

    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: C.fg }}>
        {emergencyStatusSummary.title}
      </div>
      <div style={{ fontSize: 13, color: C.fg, opacity: 0.85, marginTop: 2 }}>
        {emergencyStatusSummary.description}
      </div>
      <div style={{ fontSize: 12, color: C.mutedFg, marginTop: 2 }}>
        {emergencyStatusSummary.detail}
      </div>

      {/* Quick stats */}
      <div style={{
        display: "flex",
        gap: 12,
        marginTop: 8,
        flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: C.mutedFg }}>Status:</span>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: emergencyStatusSummary.color,
            background: emergencyStatusSummary.bg,
            padding: "2px 6px",
            borderRadius: 4,
          }}>
            {emergencyStatusSummary.level}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Clock size={12} color={C.mutedFg} />
          <span style={{ fontSize: 11, color: C.mutedFg }}>
            {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
        {emergencyAlert && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <AlertCircle size={12} color={emergencyStatusSummary.color} />
            <span style={{ fontSize: 11, color: C.mutedFg }}>
              {emergencyAlert.conditions.length} condition{emergencyAlert.conditions.length > 1 ? "s" : ""} detected
            </span>
          </div>
        )}
      </div>
    </div>
  </div>

  {/* Condition tags */}
  {emergencyAlert?.conditions?.length > 0 && (
    <div style={{
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
      marginTop: 12,
      paddingTop: 10,
      borderTop: `1px solid ${C.border}`,
    }}>
      {emergencyAlert.conditions.map((condition, idx) => (
        <span key={idx} style={{
          fontSize: 11,
          fontWeight: 500,
          color: emergencyStatusSummary.color,
          background: emergencyStatusSummary.bg,
          padding: "4px 10px",
          borderRadius: 999,
          border: `1px solid ${emergencyStatusSummary.color}25`,
        }}>
          {condition}
        </span>
      ))}
    </div>
  )}

  {/* Vitals */}
  <div style={{
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(70px, 1fr))",
    gap: 10,
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${C.border}`,
  }}>
    {[
      { label: "BP", value: toRenderableValue(emergencyStatusVitals.bp) ?? "—" },
      { label: "HR", value: toRenderableValue(emergencyStatusVitals.heartRate) ?? "—" },
      { label: "Sugar", value: toRenderableValue(emergencyStatusVitals.sugar) ?? "—" },
      { label: "Temp", value: toRenderableValue(emergencyStatusVitals.temperature) ?? "—" },
    ].map((item) => (
      <div key={item.label} style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}>
        <span style={{ fontSize: 9, color: C.mutedFg, textTransform: "uppercase" }}>
          {item.label}
        </span>
        <span style={{
          fontSize: 14,
          fontWeight: 600,
          color: item.value !== "—" ? C.fg : C.mutedFg,
        }}>
          {item.value}
        </span>
      </div>
    ))}
  </div>
</div>




        {/* Trends Section */}
        <div style={{ ...row, justifyContent: "space-between", padding: "18px 18px 8px" }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.fg }}>Trends</span>
          <button
            type="button"
            onClick={() => setShowTrendsModal(true)}
            style={{ fontSize: 12, fontWeight: 600, color: C.primary, background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
          >
            View All Trends ›
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            padding: "0 18px",
            overflowX: "auto",
            paddingBottom: 8,
          }}
        >
          {vitalsLoading ? (
            <div style={{ color: C.mutedFg, fontSize: 12, padding: "8px 0" }}>Loading trends...</div>
          ) : vitalsError ? (
            <div style={{ color: C.peachFg, fontSize: 12, padding: "8px 0" }}>Unable to load trends right now.</div>
          ) : recentVitals.length === 0 ? (
            <div style={{ color: C.mutedFg, fontSize: 12, padding: "8px 0" }}>
              No trend data available yet. Start logging your vitals to see trends.
            </div>
          ) : (
            (() => {
              // Get last 7 days
              const last7Days = [];
              const today = new Date();
              for (let i = 6; i >= 0; i--) {
                const d = new Date(today);
                d.setDate(today.getDate() - i);
                last7Days.push(d.toISOString().slice(0, 10));
              }

              // Group vitals by day
              const dayMap = new Map();
              last7Days.forEach(day => dayMap.set(day, []));
              
              recentVitals.forEach(entry => {
                const key = entry?.createdAt ? new Date(entry.createdAt).toISOString().slice(0, 10) : null;
                if (key && dayMap.has(key)) {
                  dayMap.get(key).push(entry);
                }
              });

              // Get latest values for display
              const latest = metricsDashboard?.vitals?.latest || {};
              
              // Helper to get latest value from array or direct
              const getLatestValue = (data: any, field: string) => {
                if (!data) return null;
                if (Array.isArray(data[field]) && data[field].length > 0) {
                  const latestEntry = data[field][data[field].length - 1];
                  return latestEntry?.value || latestEntry;
                }
                return data[field] ?? null;
              };

              // Calculate trends for each metric
              const getTrendPoints = (field: string, extractFn?: (entry: any) => number | null) => {
                return last7Days.map(day => {
                  const entries = dayMap.get(day) || [];
                  if (entries.length === 0) return null;
                  
                  const values: number[] = [];
                  entries.forEach(entry => {
                    let value = null;
                    if (extractFn) {
                      value = extractFn(entry);
                    } else if (Array.isArray(entry[field]) && entry[field].length > 0) {
                      const latestEntry = entry[field][entry[field].length - 1];
                      value = latestEntry?.value || latestEntry;
                    } else if (entry[field] !== undefined && entry[field] !== null) {
                      value = entry[field];
                    }
                    if (value !== null && typeof value === 'number' && !Number.isNaN(value)) {
                      values.push(value);
                    }
                  });
                  
                  if (values.length === 0) return null;
                  return Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
                });
              };

              // Get blood pressure
              const bpRaw = extractBloodPressure(latest) || extractBloodPressure(recentVitals[0]);
              
              // Get heart rate
              let latestHeartRate = null;
              if (Array.isArray(latest.heartRate) && latest.heartRate.length > 0) {
                const hr = latest.heartRate[latest.heartRate.length - 1];
                latestHeartRate = unwrapMetricValue(hr);
              } else if (recentVitals.length > 0) {
                for (let i = 0; i < recentVitals.length; i++) {
                  const entry = recentVitals[i];
                  if (Array.isArray(entry.heartRate) && entry.heartRate.length > 0) {
                    const hr = entry.heartRate[entry.heartRate.length - 1];
                    latestHeartRate = unwrapMetricValue(hr);
                    break;
                  }
                }
              }

              // Get latest sugar
              let latestSugar = null;
              if (Array.isArray(latest.bloodSugar) && latest.bloodSugar.length > 0) {
                const sugar = latest.bloodSugar[latest.bloodSugar.length - 1];
                latestSugar = unwrapMetricValue(sugar);
              } else if (recentVitals.length > 0) {
                for (let i = 0; i < recentVitals.length; i++) {
                  const entry = recentVitals[i];
                  if (Array.isArray(entry.bloodSugar) && entry.bloodSugar.length > 0) {
                    const sugar = entry.bloodSugar[entry.bloodSugar.length - 1];
                    latestSugar = unwrapMetricValue(sugar);
                    break;
                  }
                }
              }

              // Calculate trend data
              const bpPoints = getTrendPoints('bloodPressure', (entry) => {
                const bp = extractBloodPressure(entry);
                if (bp) {
                  const parsed = parseBp(bp);
                  if (parsed) return parsed.systolic;
                }
                return null;
              });

              const sugarPoints = getTrendPoints('bloodSugar');
              const hrPoints = getTrendPoints('heartRate');

              // Create trend cards with real data
              const trendCards = [];
              
              // Blood Pressure Trend
              if (bpPoints && bpPoints.some(v => v !== null)) {
                const normalizedPoints = normalizeSeries(bpPoints);
                trendCards.push({
                  key: 'bp-trend',
                  title: 'Blood Pressure',
                  value: bpRaw || '--',
                  unit: bpRaw ? 'mmHg' : undefined,
                  color: C.pinkFg,
                  fill: '#fee2e230',
                  points: normalizedPoints,
                  startLabel: shortDateLabel(last7Days[0]),
                  endLabel: shortDateLabel(last7Days[last7Days.length - 1]),
                });
              }

              // Heart Rate Trend
              if (hrPoints && hrPoints.some(v => v !== null)) {
                const normalizedPoints = normalizeSeries(hrPoints);
                const latestHr = latestHeartRate != null ? String(latestHeartRate) : '--';
                trendCards.push({
                  key: 'hr-trend',
                  title: 'Heart Rate',
                  value: latestHr,
                  unit: latestHeartRate != null ? 'bpm' : undefined,
                  color: C.blueFg,
                  fill: '#dbeafe60',
                  points: normalizedPoints,
                  startLabel: shortDateLabel(last7Days[0]),
                  endLabel: shortDateLabel(last7Days[last7Days.length - 1]),
                });
              }

              // Blood Sugar Trend
              if (sugarPoints && sugarPoints.some(v => v !== null)) {
                const normalizedPoints = normalizeSeries(sugarPoints);
                const sugar = latestSugar != null ? String(latestSugar) : '--';
                trendCards.push({
                  key: 'sugar-trend',
                  title: 'Blood Sugar',
                  value: sugar,
                  unit: latestSugar != null ? 'mg/dL' : undefined,
                  color: C.primary,
                  fill: '#f1e6ff60',
                  points: normalizedPoints,
                  startLabel: shortDateLabel(last7Days[0]),
                  endLabel: shortDateLabel(last7Days[last7Days.length - 1]),
                });
              }

              // If no trends available, show message
              if (trendCards.length === 0) {
                return (
                  <div style={{ color: C.mutedFg, fontSize: 12, padding: "8px 0" }}>
                    No trend data available yet. Continue logging your vitals.
                  </div>
                );
              }

              // Show up to 3 trend cards
              return trendCards.slice(0, 3).map((trend) => (
                <TrendCard
                  key={trend.key}
                  title={trend.title}
                  value={trend.value}
                  unit={trend.unit}
                  color={trend.color}
                  fill={trend.fill}
                  points={trend.points}
                  startLabel={trend.startLabel}
                  endLabel={trend.endLabel}
                />
              ));
            })()
          )}
        </div>

        {/* Download Health Report */}
        <div
          style={{
            margin: "16px 18px 0",
            background: C.primarySofter,
            border: `1px solid ${C.primarySoft}`,
            borderRadius: 16,
            padding: 12,
            ...row,
            gap: 10,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FileText size={20} color={C.primary} />
          </div>
          <div style={{ ...col, flex: 1 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.fg }}>Download Health Report</span>
            <span style={{ fontSize: 11, color: C.mutedFg, marginTop: 2 }}>
              Download a complete report with your vitals, trends, and health insights.
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowReportOptions(true)}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: `1px solid ${C.primary}`,
              background: C.primary,
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              ...row,
              gap: 6,
              flexShrink: 0,
            }}
          >
            <FileText size={14} /> Download
          </button>
        </div>

        <AddMedicationModal
          open={showAddMedicationModal}
          onClose={() => setShowAddMedicationModal(false)}
          onAdded={refreshRemindersAndDashboard}
        />

        <TrendsModal
          open={showTrendsModal}
          onClose={() => setShowTrendsModal(false)}
          recentVitals={recentVitals}
        />

        <ReportOptionsModal
          open={showReportOptions}
          onClose={() => setShowReportOptions(false)}
          onDownload={downloadHealthReport}
          duration={reportDuration}
          setDuration={setReportDuration}
        />

        <MedicationModal
          open={showMedicationModal}
          onClose={() => {
            setShowMedicationModal(false);
            setEditingMedication(null);
          }}
          onSave={modalMode === 'add' ? handleAddMedication : handleUpdateMedication}
          medication={editingMedication}
          mode={modalMode}
        />

        <EmergencyModal
          open={showEmergencyModal}
          onClose={dismissEmergencyAlert}
          alert={emergencyAlert}
          onDismiss={dismissEmergencyAlert}
        />
      </div>
    </div>
  );
}